<template>
  <div class="editor-page app-page">
    <header class="app-page-header editor-topbar">
      <div class="editor-top-left">
        <div class="episode-title">{{ headerTitle }}</div>
      </div>
      <div class="editor-top-right"></div>
    </header>

    <div class="editor-fixed-actions">
      <button class="back-btn" @click="goBackToStoryboardList">返回</button>
      <button
        class="back-btn"
        :disabled="!step4TimelineHydrated || batchGenerating || hasAnyVideoGenActive"
        @click="handleBatchGenerateAllVideos"
      >
        {{ batchGenerating ? "批量生成中…" : "批量生成全部片段视频" }}
      </button>
      <button
        class="main-btn export-btn"
        :disabled="exportingVideos || !step4TimelineHydrated"
        @click="handleExportEpisodeVideos"
      >
        {{ exportingVideos ? "导出中…" : "导出视频" }}
      </button>
    </div>

    <div class="editor-layout">
      <aside class="editor-left">

        <div class="left-section">
          <h3>基础设置</h3>
          <div class="field-label">模型</div>
          <select v-model="selectedModel" class="select-input" :disabled="!modelOptions.length">
            <option v-for="item in modelOptions" :key="item.value" :value="item.value">
              {{ item.label }}
            </option>
          </select>
          <div class="inline-selects">
            <select v-model="selectedDuration" class="select-input">
              <option v-for="item in durationOptions" :key="item" :value="item">
                {{ item }}
              </option>
            </select>
            <select v-model="selectedResolution" class="select-input">
              <option v-for="item in resolutionOptions" :key="item" :value="item">
                {{ item }}
              </option>
            </select>
          </div>
        </div>

        <div class="left-section">
          <h3>主体参考对象</h3>
          <p class="asset-strip-hint" v-if="false">
            分集绑定主体仅加载<strong>当前分集</strong>（与路由/当前集一致）；默认最多展示 12 张，若描述里使用 @ 引用则只显示与引用匹配的主体。用户资产库勾选仍在下方单独一行。
          </p>
          <p v-if="episodeStripHydrated && episodeStripAssetsContextHint" class="asset-strip-context-hint">
            {{ episodeStripAssetsContextHint }}
          </p>
          <div class="asset-actions">
            <button class="asset-btn" @click="openAssetModal">从资产库选择</button>
            <input
              ref="localUploadInput"
              type="file"
              accept="image/*"
              style="display: none"
              @change="onLocalUploadChange"
            />
          </div>
          <div class="asset-strip-group">
            <div class="asset-strip-caption" v-if="false">分集绑定主体</div>
            <div v-if="!episodeStripHydrated" class="asset-strip-empty">加载中…</div>
            <div v-else-if="episodeBoundStripAllEmpty" class="asset-strip-empty">暂无（当前分集暂无绑定主体，或由剧本/分镜自动关联）</div>
            <div v-else class="asset-strip-bound-rows">
              <div
                v-for="row in episodeBoundStripRows"
                :key="'bound-row-' + row.type"
                class="asset-strip-subrow"
              >
                <span class="asset-strip-type-label">{{ row.label }}</span>
                <div class="asset-strip asset-strip--sub">
                  <div
                    v-for="asset in row.items"
                    :key="'bind-' + asset.type + '-' + asset.id + '-' + asset.name"
                    class="asset-thumb"
                    :class="{ 'is-clickable': !isPlaceholderAsset(asset.cover) }"
                    :style="{ backgroundImage: 'url(' + asset.cover + ')' }"
                    role="button"
                    tabindex="0"
                    :title="isPlaceholderAsset(asset.cover) ? '' : '点击放大预览'"
                    @click="openStripPreview(asset.cover, asset)"
                    @keydown.enter.prevent="openStripPreview(asset.cover, asset)"
                  ></div>
                  <span v-if="!row.items.length" class="asset-strip-row-empty">暂无</span>
                </div>
              </div>
            </div>
          </div>
          <div class="asset-strip-group" v-if="supplementStripAssetsSafe.length>0">
            <div class="asset-strip-caption">用户资产库 / 文案 @ 引用</div>
            <div class="asset-strip">
              <div
                v-for="asset in supplementStripAssetsSafe"
                :key="'sup-' + asset.stripKind + '-' + asset.type + '-' + asset.id + '-' + asset.name"
                class="asset-thumb-wrap"
              >
                <button
                  type="button"
                  class="asset-thumb-remove"
                  title="移除此项"
                  aria-label="移除"
                  @click.stop="removeSupplementStripItem(asset)"
                >
                  ×
                </button>
                <div
                  class="asset-thumb"
                  :class="{ 'is-clickable': !isPlaceholderAsset(asset.cover) }"
                  :style="{ backgroundImage: 'url(' + asset.cover + ')' }"
                  role="button"
                  tabindex="0"
                  :title="isPlaceholderAsset(asset.cover) ? '' : '点击放大预览'"
                  @click="openStripPreview(asset.cover, asset)"
                  @keydown.enter.prevent="openStripPreview(asset.cover, asset)"
                ></div>
              </div>
              <span v-if="!episodeStripHydrated" class="asset-strip-empty">加载中…</span>
              <span v-else-if="!supplementStripAssetsSafe.length" class="asset-strip-empty">未从资产库勾选；描述里 @ 引用也会出现在此</span>
            </div>
          </div>
        </div>
      </aside>

      <section class="editor-center">
        <div class="preview-stage">
          <video
            v-if="previewVideoUrl"
            class="main-preview"
            :src="previewVideoUrl"
            controls
            playsinline
          ></video>
          <div v-else-if="isPlaceholderAsset(storyboardWideCover)" class="main-preview placeholder-block video-placeholder">
            <div class="video-placeholder-card">
              <span class="video-placeholder-play"></span>
            </div>
          </div>
          <img
            v-else
            :src="storyboardWideCover"
            :alt="storyboard.title"
            class="main-preview"
            @error="onImgError(storyboard, 'cover')"
          />
        </div>

        <div class="timeline-toolbar right-timeline-toolbar">
          <h3>分镜描述</h3>
          <div class="timeline-time">{{ timelineToolbarTime }}</div>
        </div>

        <div v-if="promptQuickTemplates.length&&false" class="prompt-template-bar">
          <span class="prompt-template-label">场景模板</span>
          <button
            v-for="(tpl, idx) in promptQuickTemplates"
            :key="'ptpl-' + idx"
            type="button"
            class="secondary-btn prompt-tpl-btn"
            @click="appendPromptTemplate(tpl.text)"
          >
            {{ tpl.label }}
          </button>
        </div>

        <div class="center-desc-wrap">
          
          <div class="prompt-editor-wrap">
            <div
              :key="'prompt-editor-shot-' + (storyboard && storyboard.id ? storyboard.id : '0')"
              ref="promptEditor"
              class="desc-box prompt-editor"
              contenteditable="true"
              role="textbox"
              data-placeholder="在此输入镜头描述；时长用 3s 或 3秒；对白用自然写法如「@张三 说：…」「旁白：…」；输入 @ 引用角色/场景/素材"
              @input="onPromptInput"
              @keydown="onPromptKeydown"
              @blur="onPromptBlur"
            ></div>
            <div v-if="mentionVisible" class="mention-panel">
              <button
                v-for="item in mentionOptions"
                :key="item.type + '-' + item.id"
                type="button"
                class="mention-item"
                @mousedown.prevent="insertMention(item)"
              >
                <span class="mention-item-main">
                  <img
                    v-if="item.cover && !isPlaceholderAsset(item.cover)"
                    class="mention-thumb"
                    :src="mediaUrl(item.cover)"
                    :alt="item.name"
                  />
                  <span v-else class="mention-thumb mention-thumb--empty placeholder-block" />
                  <span class="mention-name">@{{ item.name }}</span>
                </span>
                <span class="mention-type">{{ mentionTypeLabel(item.type) }}</span>
              </button>
              <div v-if="!mentionOptions.length" class="mention-empty">暂无可引用素材，请先在资产库中选择或上传</div>
            </div>
          </div>
          <div class="desc-footer-actions">
            <button
              class="secondary-btn slim-btn"
              :disabled="!canExpandCurrentDescription"
              :title="expandCurrentDescriptionHint"
              @click="handleExpandCurrentDescription"
            >
              <span v-if="expandingDescription">{{ expandProgressPct }}% {{ expandProgressMessage || '充实中…' }}</span>
              <span v-else>扩充描述</span>
            </button>
            <span
              v-if="descriptionCharCount > 0 && descriptionCharCount < descriptionCharMin"
              class="desc-short-warning"
              :title="'当前描述过短，建议 ≥ ' + descriptionCharMin + ' 字'"
            >
              描述过短（{{ descriptionCharCount }}/{{ descriptionCharMin }}）
            </span>
            <button
              class="main-btn desc-generate-btn"
              :disabled="!step4TimelineHydrated || isGenerateVideoButtonLocked || videoPointsPerSecond <= 0"
              @click="handleGenerateVideo"
            >
              {{ generateButtonText }}<span class="desc-points-text">（积分 {{ generatePointsDisplay }}）</span>
            </button>
          </div>
        </div>
      </section>

      <aside class="editor-right">
        <h3>片段视频</h3>
        <p class="hint">点击分镜切换；上方按钮可单独重新生成或跳过本镜。</p>

        <div class="storyboard-list-scroll">
          <div
            v-for="item in timelineItems"
            :key="'right-' + item.id"
            class="storyboard-list-item"
            :class="{ active: item.id === storyboard.id }"
            @click="selectTimelineItem(item)"
          >
            <div v-if="item.id === storyboard.id" class="shot-actions-row">
              <button
                type="button"
                class="shot-action-btn shot-action-regen"
                :disabled="isShotVideoGenerating(item.id) || videoPointsPerSecond <= 0"
                :title="hasShotVideoUrl(item) ? '重新生成本镜视频' : '仅生成本镜视频'"
                @click.stop="handleRegenerateSingleShot(item)"
              >↻ {{ hasShotVideoUrl(item) ? '重新生成本镜' : '仅生成本镜' }}</button>
              <button
                type="button"
                class="shot-action-btn shot-action-skip"
                :class="{ on: shotIsSkipped(item) }"
                :disabled="isShotVideoGenerating(item.id)"
                :title="shotIsSkipped(item) ? '取消跳过本镜' : '跳过本镜（手动跳错/不需要生成）'"
                @click.stop="toggleShotSkip(item)"
              >⤴ {{ shotIsSkipped(item) ? '已跳过' : '跳过本镜' }}</button>
            </div>
            <div class="storyboard-thumb-wrap">
              <img
                v-if="item.videoCoverUrl"
                class="storyboard-thumb"
                :src="item.videoCoverUrl"
                :alt="item.title"
                loading="lazy"
                decoding="async"
              />
              <div v-else-if="isPlaceholderAsset(item.cover)" class="storyboard-thumb placeholder-block video-placeholder">
                <div class="video-placeholder-card">
                  <span class="video-placeholder-play"></span>
                </div>
              </div>
              <img
                v-else
                class="storyboard-thumb"
                :src="item.cover"
                :alt="item.title"
                loading="lazy"
                decoding="async"
                @error="onImgError(item, 'cover')"
              />
              <div v-if="shotIsSkipped(item)" class="storyboard-skip-badge">已跳过</div>
              <div class="storyboard-item-actions">
                <button class="icon-btn" title="历史记录" @click.stop="openRecordModal(item)" v-if="false">👁</button>
                <button
                  v-if="historyRecordVideoUrl(item)"
                  type="button"
                  class="icon-btn record-download-btn"
                  title="下载视频"
                  @click.stop="downloadHistoryVideo(item)"
                >
                  ⬇
                </button>
                <button class="icon-btn" title="删除分镜" @click.stop="deleteStoryboardItem(item)">🗑</button>
              </div>
            </div>
            <div
              class="storyboard-item-meta"
              :class="{
                'is-generating': isShotVideoGenerating(item.id),
                'is-failed': isShotVideoGenFailed(item.id),
                'is-done': isShotVideoGenDone(item),
                'is-idle': isShotVideoNotGenerated(item)
              }"
            >
              <strong>#{{ item.shotNo || item.id }}</strong>
              <div class="storyboard-meta-right">
                <span class="storyboard-gen-tag" :class="shotVideoGenBadgeClass(item)">{{
                  shotVideoGenBadgeText(item)
                }}</span>
                <small>{{ item.durationText }}</small>
              </div>
            </div>
          </div>
        </div>
        <div class="editor-right-footer">
          <button
            type="button"
            class="secondary-btn slim-btn next-episode-btn"
            :disabled="!nextEpisodeAvailable || nextEpisodeLoading"
            :title="nextEpisodeAvailable ? `继续编辑：${nextEpisodeLabel}` : '已是最后一集'"
            @click="goToNextEpisode"
          >
            <span v-if="!nextEpisodeLoading">继续编辑下集 →</span>
            <span v-else>加载中…</span>
            <em v-if="nextEpisodeAvailable" class="next-episode-sub">{{ nextEpisodeLabel }}</em>
          </button>
        </div>
      </aside>
    </div>

    <div v-if="assetModalVisible" class="mask" @click.self="closeAssetModal">
      <div class="modal modal-xl">
        <button class="modal-close" @click="closeAssetModal">×</button>
        <div class="asset-modal-header">
          <h3>用户资产库</h3>
          <button class="asset-btn" @click="uploadLocal">本地上传</button>
        </div>
        <p class="asset-modal-sub">仅展示您账号下的独立素材，不包含当前分集已绑定主体；勾选后将作为本镜头的额外参考。</p>
        <div class="tabs tabs-with-gap">
          <button
            v-for="type in assetTypes"
            :key="type.value"
            class="tab-btn"
            :class="{ active: activeAssetType === type.value }"
            @click="switchAssetModalTab(type.value)"
          >
            {{ type.label }}
          </button>
        </div>
        <div class="asset-library-grid">
          <div
            v-for="item in filteredModalAssets"
            :key="item.id"
            class="asset-library-item"
            :class="{ active: isTempAssetSelected(item.id) }"
            @click="toggleTempAsset(item.id)"
          >
            <div v-if="isPlaceholderAsset(item.cover)" class="asset-library-thumb placeholder-block"></div>
            <img v-else class="asset-library-thumb" :src="item.cover" :alt="item.name" />
            <span>{{ item.name }}</span>
          </div>
        </div>
        <div class="modal-actions modal-actions-equal">
          <button type="button" class="secondary-btn modal-footer-btn" @click="closeAssetModal">取消</button>
          <button type="button" class="main-btn modal-main-btn modal-footer-btn" @click="confirmAssets">确认选择</button>
        </div>
      </div>
    </div>

    <div v-if="recordModalVisible" class="mask" @click.self="closeRecordModal">
      <div class="modal modal-lg">
        <button class="modal-close" @click="closeRecordModal">×</button>
        <h3>历史生成记录</h3>
        <div v-if="currentRecordList.length" class="record-modal-list">
          <div v-for="record in currentRecordList" :key="record.id" class="record-modal-item">
            <button
              v-if="historyRecordVideoUrl(record)"
              type="button"
              class="icon-btn record-download-btn"
              title="下载视频"
              @click.stop="downloadHistoryVideo(record)"
            >
              ⬇
            </button>
            <div v-if="isPlaceholderAsset(record.cover)" class="record-modal-thumb placeholder-block"></div>
            <img v-else class="record-modal-thumb" :src="record.cover" :alt="record.model" />
            <div class="record-modal-body">
              <strong>{{ formatModelLabel(record.model) }}</strong>
              <small>{{ record.time }}</small>
              <div class="record-status">
                <span>{{ record.duration }}</span>
                <span class="success-tag">{{ record.status }}</span>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="empty-record">暂无历史记录</div>
        <div class="modal-actions">
          <button class="main-btn modal-main-btn" @click="closeRecordModal">关闭</button>
        </div>
      </div>
    </div>

    <ImagePreviewLayer
      :visible="imagePreviewLayer.visible"
      :src="imagePreviewLayer.src"
      :alt="imagePreviewLayer.alt"
      @close="closeImagePreviewLayer"
      @img-error="onImagePreviewLayerImgError"
    />
  </div>
</template>

<script>
import { userApi } from "../api/user";
import ImagePreviewLayer from "../components/ImagePreviewLayer.vue";
import { alertStep4VideoError, mapStep4VideoErrorHint } from "../utils/step4VideoErrorHint";
import { setPollingTimeout, clearPollingTimeout, expBackoff, jitterDelay } from "../utils/pollHelpers";
import { kvStore } from "../utils/kvStore";
import {
  LEGACY_SESSION_KEYS,
  projectStepCacheKey,
  storyboardsPrimaryCacheKey,
  storyboardsStorageKeys
} from "../constants/projectCache";

/**
 * 分镜编辑页（/project/storyboard-editor）：步骤四镜头描述、视频模型/时长/分辨率、分集实体绑定与 @ 引用。
 * 数据：projectDetail 回填 shots 后 step4TimelineHydrated 为真；分集绑定主体走 listProjectStoryboards(episodeId)（旧名 generateProjectStep3Storyboards 已重命名）；
 * 编辑器为 contenteditable，切换分镜时注意 :key 与 $nextTick 同步 innerText（见 hydrateShotsFromProjectDetail / selectTimelineItem）。
 */
/**
 * 模拟：视频生成「积分/秒」= f(分辨率, 模型)。480p / 720p 与产品说明一致；1080p 为占位延伸，后续由后端配置接口下发完整价目表。
 */
/** 接口无数据或未登录时的时长/分辨率兜底 */
const DEFAULT_DURATION_OPTIONS = ["4s", "5s", "6s", "7s", "8s", "9s", "10s", "11s", "12s", "13s", "14s", "15s"];
const DEFAULT_RESOLUTION_OPTIONS = ["480p", "720p", "1080p"];

/** 接口/存储里 prompt 常被 HTML 转义或双重转义（如 &amp;lt;duration-ms&amp;gt;），需逐层解码再识别真实标签 */
function decodeHtmlEntitiesRepeated(s) {
  let out = String(s || "");
  for (let i = 0; i < 8; i++) {
    if (typeof document === "undefined") break;
    const ta = document.createElement("textarea");
    ta.innerHTML = out;
    const next = ta.value;
    if (next === out) break;
    out = next;
  }
  return out;
}

/** 旧版 <duration-ms> 转为「Ns」展示；新正文直接保留 3s/3秒 等自然写法 */
function normalizeShotPromptDurationForDisplay(text) {
  return decodeHtmlEntitiesRepeated(text).replace(/<duration-ms>\s*(\d+)\s*<\/duration-ms>/gi, (_, ms) => {
    const n = Number(ms);
    if (!Number.isFinite(n) || n <= 0) return "1s";
    return `${Math.max(1, Math.round(n / 1000))}s`;
  });
}

/** 用户编辑区不展示「生成语言」行；成片语言在步骤四生成视频时由后端注入 */
function stripVideoLanguageLineForPrompt(text) {
  return String(text || "")
    .replace(/^\s*生成语言\s*[：:][^\n]*\n?/gm, "")
    .replace(/^\s*\[(?:语言|Language)\][^\n]*\n(?:\n)?/gm, "")
    .trim();
}

/** 分镜 prompt 入库不含画风固定首行；展示/步骤四出视频时由平台拼接 */
function stripShotPromptStyleLineForPrompt(text) {
  return String(text || "")
    .replace(/^(?:\s*画面风格\s*[:：][^\n]*(?:\n|$))+/gm, "")
    .trim();
}

/**
 * 分镜 prompt 去掉「1-1 【15秒】」「3-2【12秒】」等镜号段（仅剥离开头第一段）：
 *  - 这是步骤一重生成的元信息头，只用于生成阶段对齐，不应出现在最终 prompt 内容里；
 *  - 真实时长已由 fa_ai_project_shot.duration_seconds 单独记录；
 *  - 只影响 prompt 头一镜号段，不动中间正文。
 */
const SHOT_PROMPT_HEADER_REGEX = /^\s*\d+\s*-\s*\d+\s*[【\[\(]\s*\d{1,2}\s*(?:秒|s|sec)?\s*[】\]\)]\s*(?:\r?\n)?/i;
function stripShotPromptEpisodeHeaderForPrompt(text) {
  return String(text || "").replace(SHOT_PROMPT_HEADER_REGEX, "").trim();
}

// 说明：不做“写死价格兜底”，价格必须来自后端（fa_ai_points_config）。
// 若拿不到 pricing，则禁用生成按钮并提示未配置扣费。

export default {
  name: "StoryboardEditorPage",
  components: {
    ImagePreviewLayer
  },
  data() {
    const storyboardId = Number(this.$route.query.id || 0);
    const projectId = Number(this.$route.query.projectId || 0);
    const cacheKey = storyboardsPrimaryCacheKey(projectId);
    let cached = [];
    try {
      cached = JSON.parse(
        sessionStorage.getItem(cacheKey) || sessionStorage.getItem(LEGACY_SESSION_KEYS.storyboards) || "[]"
      );
    } catch (e) {
      cached = [];
    }
    const fallbackEpisode = cached[0] || {
      id: 1,
      title: "第 1 集：废柴逆袭 苍王降临",
      cover: "https://dummyimage.com/108x146/4a3c2f/fff.png&text=1",
      duration: "6 分钟",
      episodeScript: ""
    };
    const episode = cached.find(item => Number(item.id) === Number(storyboardId)) || fallbackEpisode;
    const shots = Array.isArray(episode.shots) ? episode.shots : [];
    const shotItems =
      shots.length > 0
        ? shots.map((s, idx) => ({
            id: Number((s && s.id) || 0),
            shotNo: Number((s && (s.no || s.shotNo)) || (idx + 1)),
            title: s.title || `画面 ${idx + 1}`,
            // 右侧分镜缩略图：不用分集 cover / 九宫格兜底，避免未生成视频时也显示图片（与步骤三分集卡片回填区分）
            cover: String((s && s.cover) || "").trim(),
            videoUrl: String((s && (s.videoUrl || s.video_url)) || "").trim(),
            videoCoverUrl: String((s && (s.videoCoverUrl || s.video_cover_url)) || "").trim(),
            durationSeconds: typeof s.durationSeconds === "number" ? s.durationSeconds : 5,
            durationText: `${typeof s.durationSeconds === "number" ? s.durationSeconds : 5}s`,
            prompt: s.prompt || s.description || ""
          }))
        : [];
    const firstShotDurationText = String((shotItems[0] && shotItems[0].durationText) || "").trim() || "15s";

    return {
      headerTitle: episode.title,
      episode,
      storyboard: shotItems[0] || {
        id: 1,
        shotNo: 1,
        title: "画面 1",
        cover: "",
        durationSeconds: 5,
        durationText: "5s",
        prompt: ""
      },
      projectId,
      selectedModel: "",
      selectedDuration: firstShotDurationText,
      selectedResolution: "720p",
      description: normalizeShotPromptDurationForDisplay(
        String(
          stripShotPromptEpisodeHeaderForPrompt(
            stripShotPromptStyleLineForPrompt(
              stripVideoLanguageLineForPrompt(String((shotItems[0] && shotItems[0].prompt) || ""))
            )
          )
        ).replace(/\r\n/g, "\n").replace(/\n{2,}/g, "\n")
      ),
      mentionVisible: false,
      mentionQuery: "",
      // 右侧应展示「分镜/画面（shots）」而不是分集（episodes）
      timelineItems: shotItems.length
        ? shotItems
        : [
            {
              id: 1,
              shotId: 0,
              title: "画面 1",
              cover: "",
              durationSeconds: 5,
              durationText: "5s",
              prompt: ""
            }
          ],
      /** 来自 GET videoModels（fa_ai_model.model_type=video） */
      videoModelsRaw: [],
      assetModalVisible: false,
      activeAssetType: "role",
      assetTypes: [
        { value: "role", label: "角色" },
        { value: "scene", label: "场景" },
        { value: "material", label: "素材" }
      ],
      /** 登录后由 assets 接口填充；未登录为空 */
      assetLibrary: [],
      /** 第 1 步提取 + 第 2 步生成并关联的项目资源（角色/场景/素材），用于 asset-strip 预设展示与 @ 引用 */
      projectEntityLibrary: [],
      /** 当前任务独立素材池（仅当前镜头任务） */
      taskAssetLibrary: [],
      /** 后端返回：当前分集已绑定实体 */
      currentEpisodeEntities: [],
      /**
       * 首屏在 listProjectStoryboards 返回前，若先渲染「文案 @」会与分集实体同名同图，
       * 会短暂出现在「用户资产库」行；待实体列表到位后再展示两行缩略条，避免错位闪烁。
       */
      episodeStripHydrated: false,
      tempSelectedAssetIds: [],
      /** 资产库弹窗内按类型的勾选草稿（切换 Tab 时不丢失） */
      modalAssetSelectionDraft: { role: [], scene: [], material: [] },
      imagePreviewLayer: {
        visible: false,
        src: "",
        alt: "预览",
        errorItem: null,
        errorField: "cover"
      },
      recordModalVisible: false,
      currentRecordItemId: null,
      historyRecordsByStoryboardId: {},
      /** 正在生成视频的分镜 id；与 videoGenByShotId 同步，兼容旧逻辑 */
      generatingVideoStoryboardId: null,
      /** shotId → { taskId, status, progress, message }，刷新后由 listStep4VideoTasks 恢复 */
      videoGenByShotId: {},
      /** 避免同一分镜重复启动轮询 */
      _activeVideoGenPolls: {},
      /** 共享后台批量轮询定时器：所有 shot 一起跑一次 batchGetGenerateStoryboardVideoTaskStatus */
      _batchVideoPollTimer: null,
      _batchVideoPollInFlight: false,
      /** 步骤四后台轮询间隔（ms），随任务数自适应 */
      _step4BatchPollMs: 20000,
      /** hydrate 时延后写 storyboards 缓存：避免主线程 JSON.stringify 阻塞 30~80ms */
      _pendingStoryboardsCacheWrite: null,
      _pendingStoryboardsCacheTimer: null,
      /** 当前分集中标记「跳过」的分镜 id（不生成视频） */
      skippedShotIds: [],
      nextEpisodeInfo: null,
      nextEpisodeLoading: false,
      batchGenerating: false,
      exportingVideos: false,
      includeStripAssetsAsReferences: true,
      projectMetaDraft: null,
      settingsSaveTimer: null,
      hasSavedStep4Selection: false,
      // 单条分镜描述充实（异步任务期间）
      expandingDescription: false,
      /** 异步扩充分镜描述任务进度 (0-100) */
      expandProgressPct: 0,
      /** 异步扩充分镜描述任务当前消息 */
      expandProgressMessage: '',
      /** 轮询定时器句柄（在页面销毁时清理） */
      _expandProgressTimer: null,
      /** 单条分镜「扩充描述」异步任务的最新 taskId（页面刷新用于恢复） */
      _expandTaskIdsByShot: {},
      descriptionCharMin: 100,
      step4AutoSaveReady: false,
      /** projectDetail 已回填当前分集 shots（避免首屏仍用 session 里的错误 shot.id / episode.id 调 saveStoryboardVideoConfig） */
      step4TimelineHydrated: false,
      step4SyncingSelection: false,
      previewVideoUrl: "",
      /** @ 输入时防抖写入 sessionStorage，降低 main thread JSON 压力 */
      _shotPromptSessionTimer: null,
      /** 点击分镜列表时取消 blur 延迟任务，避免 120ms 后把旧 DOM 写回新选中分镜（有视频时尤甚） */
      _promptBlurTimer: null,
      /** 切换分镜后短时间内忽略 blur 触发的 sync，防止与 preview 区挂载 video 抢焦点时序打架 */
      _suppressPromptBlurSyncUntil: 0,
      /** 项目类型（来自 projectDetail），用于分镜页文案模板 */
      projectSceneType: "",
      /** 步骤四出视频时注入的画风行（projectDetail.shotPromptStyleLine） */
      shotPromptStyleLine: "",
      visualStyleLabel: ""
    };
  },
  computed: {
    nextEpisodeAvailable() {
      // 仅基于当前项目缓存判断是否还有下一集；初次进入若缓存为空，会在 goToNextEpisode 中实时拉取
      return Boolean(this.nextEpisodeInfo);
    },
    nextEpisodeLabel() {
      const n = this.nextEpisodeInfo;
      if (!n) return "已是最后一集";
      const no = Number(n.no || n.episodeNo) || '';
      const title = n.title ? String(n.title) : '';
      return no ? `第 ${no} 集${title ? '：' + title : ''}` : title || '下一集';
    },
    /** 分集侧已绑定主体（服务端） */
    episodeStripAssets() {
      if (!this.episodeStripHydrated) return [];
      const out = [];
      const seen = new Set();
      (this.currentEpisodeEntities || []).forEach(it => {
        if (String(it.bindSource || "") === "manual_asset") return;
        const t = this.normalizeMentionEntityType(it.type);
        const k = `${t}:${it.name}`;
        if (!it.name || seen.has(k)) return;
        seen.add(k);
        out.push({
          id: it.id,
          type: t,
          name: String(it.name || ""),
          cover: it.cover || ""
        });
      });
      return out;
    },
    /** 与 loadEpisodeEntitiesFromApi 使用同一分集 id，供 watch 触发刷新 */
    activeEpisodeIdForStrip() {
      return this.resolveActiveEpisodeId();
    },
    /** 按项目类型给出可一键追加的镜头描述片段（不覆盖已有正文） */
    promptQuickTemplates() {
      const t = String(this.projectSceneType || "").trim();
      if (t === "商品讲解") {
        return [
          {
            label: "商品展示",
            text:
              "【分镜1】4秒：固定镜头，中景，@场景名 / @素材名，柔和顶光，商品主体居中、细节清晰。旁白：沉稳男声介绍产品核心卖点与使用场景。"
          },
          {
            label: "细节特写",
            text:
              "【分镜1】5秒：微推镜头，特写，@素材名，突出材质与工艺细节，背景虚化。旁白：亲切女声强调差异化优势。"
          }
        ];
      }
      if (t === "MG动画" || t.toLowerCase().includes("mg")) {
        return [
          {
            label: "MG 开场",
            text:
              "【分镜1】4秒：固定镜头，全画幅图形块面入场，扁平配色，@场景名 为抽象信息背景。旁白：轻快一句话点题。"
          },
          {
            label: "图标动效",
            text:
              "【分镜1】5秒：摇移镜头，@场景名 / @素材名，简洁图标与路径动画，无写实人脸。本段无对白。"
          }
        ];
      }
      if (t.indexOf("旁白") !== -1) {
        return [
          {
            label: "企宣段落",
            text:
              "【分镜1】5秒：拉远镜头，大全景，@场景名，品牌氛围光、空间纵深。旁白：大气稳重念出企业愿景与价值主张。"
          }
        ];
      }
      return [];
    },
    /**
     * 分集绑定主体条实际展示列表：有 @ 时优先只显示名称命中的绑定图；否则最多 12 张，避免一屏几十张。
     * 全量合并见 {@link assetStripAssets}（分集绑定 + context 补全 + supplement，按 type:name 去重）。
     */
    episodeStripAssetsForContext() {
      const full = Array.isArray(this.episodeStripAssets) ? this.episodeStripAssets : [];
      const MAX = 12;
      const atNames = this.extractAtNames(this.description || "");
      let list;
      if (atNames.length) {
        const set = new Set(atNames);
        const matched = full.filter(a => set.has(String(a.name || "").trim()));
        const seenKeys = new Set(
          matched.map(a => `${this.normalizeMentionEntityType(a.type)}:${String(a.name || "").trim()}`)
        );
        const fromProject = [];
        (this.projectEntityLibrary || []).forEach(p => {
          const name = String(p.name || "").trim();
          if (!name || !set.has(name)) return;
          const t = this.normalizeMentionEntityType(p.type);
          const k = `${t}:${name}`;
          if (seenKeys.has(k)) return;
          seenKeys.add(k);
          fromProject.push({
            id: p.id,
            type: t,
            name,
            cover: String(p.cover || "").trim()
          });
        });
        const merged = [...matched, ...fromProject];
        list = merged.length ? merged : full;
      } else {
        list = full;
      }
      return list.slice(0, MAX);
    },
    episodeStripAssetsContextHint() {
      if (!this.episodeStripHydrated) return "";
      const full = Array.isArray(this.episodeStripAssets) ? this.episodeStripAssets : [];
      const MAX = 12;
      const atNames = this.extractAtNames(this.description || "");
      if (atNames.length) {
        const shown = new Set(
          (this.episodeStripAssetsForContext || []).map(a => String((a && a.name) || "").trim()).filter(Boolean)
        );
        const unresolved = atNames.filter(n => !shown.has(n));
        if (unresolved.length && full.length) {
          return "描述中的 @ 未匹配到本分集绑定主体，已改为展示本分集绑定预览（最多 12 张）。";
        }
      }
      if (full.length > MAX) {
        return atNames.length
          ? "绑定主体较多，已按 @ 匹配并最多展示 12 张。"
          : "本分集绑定主体较多，此处最多展示 12 张；在描述中用 @ 名称可优先筛选相关主体。";
      }
      return "";
    },
    /** 用户资产库勾选 + 描述中 @ 引用 + 服务端 manual_asset 分集绑定（刷新后仍显示在下方） */
    supplementStripAssets() {
      if (!this.episodeStripHydrated) return [];
      const seen = new Set();
      const markSeen = a => {
        if (!a || !String(a.name || "").trim()) return;
        const t = this.normalizeMentionEntityType(a.type);
        seen.add(`${t}:${String(a.name || "").trim()}`);
      };
      /** 与「分集绑定主体」当前展示一致（含从项目实体补全的项），避免下方再重复同一 @ 图 */
      (this.episodeStripAssetsForContext || []).forEach(markSeen);
      const out = [];
      (this.taskAssetLibrary || []).forEach(it => {
        const t = this.normalizeMentionEntityType(it.type);
        const k = `${t}:${String(it.name || "").trim()}`;
        if (seen.has(k)) return;
        seen.add(k);
        out.push({ ...it, type: t, stripKind: "task" });
      });
      (this.currentEpisodeEntities || []).forEach(it => {
        if (String(it.bindSource || "") !== "manual_asset") return;
        const t = this.normalizeMentionEntityType(it.type);
        const k = `${t}:${String(it.name || "").trim()}`;
        if (!it.name || seen.has(k)) return;
        seen.add(k);
        out.push({
          id: it.id,
          type: t,
          name: String(it.name || ""),
          cover: it.cover || "",
          stripKind: "manual_episode"
        });
      });
      const names = this.extractAtNames(this.description || "");
      const byName = this.mentionAssetByNameFirst;
      names.forEach(n => {
        const hit = byName.get(n);
        if (!hit) return;
        const t = this.normalizeMentionEntityType(hit.type);
        const k = `${t}:${String(hit.name || "").trim()}`;
        if (seen.has(k)) return;
        seen.add(k);
        out.push({ ...hit, type: t, stripKind: "mention" });
      });
      return out;
    },
    /** 合并列表：用于生成请求 references 等；去重键与 supplement 一致，并纳入 context 补全项（避免仅靠 supplement 漏掉仅在上条展示的实体） */
    assetStripAssets() {
      if (!this.episodeStripHydrated) return [];
      const byKey = new Map();
      const add = item => {
        if (!item || !String(item.name || "").trim()) return;
        const t = this.normalizeMentionEntityType(item.type);
        const k = `${t}:${String(item.name || "").trim()}`;
        if (byKey.has(k)) return;
        byKey.set(k, { ...item, type: t });
      };
      (this.episodeStripAssets || []).forEach(add);
      (this.episodeStripAssetsForContext || []).forEach(add);
      (this.supplementStripAssets || []).forEach(add);
      return Array.from(byKey.values());
    },
    /** 模板兜底，避免热更新/合并异常时出现 undefined.length */
    episodeStripAssetsSafe() {
      return Array.isArray(this.episodeStripAssets) ? this.episodeStripAssets : [];
    },
    /** 分集绑定主体按 role/scene/material 分行 */
    episodeBoundStripRows() {
      const rows = [
        { type: "role", label: "角色", items: [] },
        { type: "scene", label: "场景", items: [] },
        { type: "material", label: "素材", items: [] }
      ];
      const byT = { role: rows[0].items, scene: rows[1].items, material: rows[2].items };
      (this.episodeStripAssetsForContext || []).forEach(a => {
        const t = this.normalizeMentionEntityType(a && a.type);
        if (byT[t]) byT[t].push(a);
      });
      return rows;
    },
    episodeBoundStripAllEmpty() {
      if (!this.episodeStripHydrated) return false;
      return !(this.episodeStripAssetsSafe || []).length;
    },
    supplementStripAssetsSafe() {
      return Array.isArray(this.supplementStripAssets) ? this.supplementStripAssets : [];
    },
    storyboardWideCover() {
      // 原型阶段使用封面图推导大图预览，后续可替换为真实视频帧或视频地址。
      return this.storyboard.cover.replace("108x146", "760x430");
    },
    modelOptions() {
      const raw = Array.isArray(this.videoModelsRaw) ? this.videoModelsRaw : [];
      return raw
        .map(m => ({
          value: String((m && m.code) || ""),
          label: String((m && (m.name || m.code)) || "")
        }))
        .filter(x => x.value);
    },
    /** 仅统计已有 video_url 的分镜：当前时间轴上「已产出视频」总时长；左侧为当前选中分镜之前（不含当前）的累计 */
    timelineTotalSecondsWithVideo() {
      let sum = 0;
      (this.timelineItems || []).forEach(it => {
        const vu = String((it && it.videoUrl) || "").trim();
        if (!vu) return;
        sum += this.shotPlanDurationSeconds(it);
      });
      return sum;
    },
    timelineSecondsWithVideoBeforeCurrent() {
      const list = Array.isArray(this.timelineItems) ? this.timelineItems : [];
      const curId = Number((this.storyboard && this.storyboard.id) || 0);
      let sum = 0;
      for (let i = 0; i < list.length; i++) {
        const it = list[i];
        if (Number((it && it.id) || 0) === curId) break;
        const vu = String((it && it.videoUrl) || "").trim();
        if (!vu) continue;
        sum += this.shotPlanDurationSeconds(it);
      }
      return sum;
    },
    timelineToolbarTime() {
      const a = this.timelineSecondsWithVideoBeforeCurrent;
      const b = this.timelineTotalSecondsWithVideo;
      return `${this.formatToolbarMmSs(a)} / ${this.formatToolbarMmSs(b)}`;
    },
    /** 仅当「当前选中分镜」正在生成时锁定生成按钮；切换其它分镜可继续生成 */
    isGenerateVideoButtonLocked() {
      const sid = Number((this.storyboard && this.storyboard.id) || 0);
      return this.batchGenerating || this.isShotVideoGenerating(sid);
    },
    hasAnyVideoGenActive() {
      const map = this.videoGenByShotId || {};
      return Object.keys(map).some(k => this.isShotVideoGenerating(k));
    },
    currentVideoModel() {
      const list = this.videoModelsRaw || [];
      return list.find(m => m.code === this.selectedModel) || null;
    },
    durationOptions() {
      const m = this.currentVideoModel;
      if (m && Array.isArray(m.durations) && m.durations.length) {
        return m.durations;
      }
      return DEFAULT_DURATION_OPTIONS;
    },
    resolutionOptions() {
      const m = this.currentVideoModel;
      if (m && Array.isArray(m.resolutions) && m.resolutions.length) {
        return m.resolutions;
      }
      return DEFAULT_RESOLUTION_OPTIONS;
    },
    /** 弹窗：仅用户独立资产库（fa_ai_asset），不含分集绑定图、不含项目实体库 */
    filteredModalAssets() {
      const source = this.assetLibrary || [];
      return source.filter(item => item.type === this.activeAssetType);
    },
    mentionSourceLibrary() {
      // 宽表：供序列化/引用匹配（含项目全量实体与用户资产库），与 @ 下拉「可选范围」无关
      const byKey = new Map();
      const add = x => {
        if (!x || !String(x.name || "").trim()) return;
        const nt = this.normalizeMentionEntityType(x.type);
        const k = `${nt}:${String(x.name || "").trim()}`;
        if (!byKey.has(k)) byKey.set(k, { ...x, type: nt });
      };
      (this.currentEpisodeEntities || []).forEach(add);
      (this.projectEntityLibrary || []).forEach(add);
      (this.taskAssetLibrary || []).forEach(add);
      (this.assetLibrary || []).forEach(add);
      return Array.from(byKey.values());
    },
    /**
     * 纯文本 @ 自动成卡 / supplement 条解析用的「按名称首条」映射。
     * 顺序：分集绑定 → 任务池 → 分集 manual_asset → 项目实体库（补全剧本 @ 了但未写入分集绑定的角色/场景等）。
     */
    mentionAssetByNameFirst() {
      const byName = new Map();
      const tryAdd = item => {
        if (!item || !String(item.name || "").trim()) return;
        const name = String(item.name || "").trim();
        if (byName.has(name)) return;
        const nt = this.normalizeMentionEntityType(item.type);
        byName.set(name, { ...item, type: nt });
      };
      (this.episodeStripAssets || []).forEach(tryAdd);
      (this.taskAssetLibrary || []).forEach(tryAdd);
      (this.currentEpisodeEntities || []).forEach(it => {
        if (String(it.bindSource || "") !== "manual_asset") return;
        const row = this.resolveAssetLibraryRow(it.type, it.name);
        tryAdd({
          id: it.id,
          type: it.type || "material",
          name: String(it.name || ""),
          cover: row && row.cover ? this.mediaUrl(row.cover) : it.cover || ""
        });
      });
      (this.projectEntityLibrary || []).forEach(p => {
        tryAdd({
          id: p.id,
          type: p.type || "material",
          name: String(p.name || "").trim(),
          cover: String(p.cover || "").trim()
        });
      });
      return byName;
    },
    /** @ 下拉候选：严格与左侧两条 UI 一致 = episodeBoundStripRows（分集绑定展示）+ supplementStripAssetsSafe（用户资产库/文案 @ 引用） */
    mentionPickerLibrary() {
      const byKey = new Map();
      const add = x => {
        if (!x || !String(x.name || "").trim()) return;
        const nt = this.normalizeMentionEntityType(x.type);
        const k = `${nt}:${String(x.name || "").trim()}`;
        if (!byKey.has(k)) byKey.set(k, { ...x, type: nt });
      };
      (this.episodeBoundStripRows || []).forEach(row => {
        (row && Array.isArray(row.items) ? row.items : []).forEach(add);
      });
      (this.supplementStripAssetsSafe || []).forEach(add);
      return Array.from(byKey.values());
    },
    currentRecordList() {
      if (!this.currentRecordItemId) return [];
      return this.historyRecordsByStoryboardId[this.currentRecordItemId] || [];
    },
    mentionOptions() {
      const keyword = this.mentionQuery.trim().toLowerCase();
      const rolesSource = (this.mentionPickerLibrary || []).filter(
        item => this.normalizeMentionEntityType(item.type) === "role"
      );
      const otherSource = (this.mentionPickerLibrary || []).filter(
        item => ["scene", "material"].indexOf(this.normalizeMentionEntityType(item.type)) !== -1
      );
      const roleItems = rolesSource.map(role => ({
        id: role.id,
        type: "role",
        name: role.name,
        cover: role.preview || role.avatar || "",
        description: role.description
      }));
      const otherItems = otherSource.map(it => ({
        id: it.id,
        type: this.normalizeMentionEntityType(it.type),
        name: it.name,
        cover: it.preview || it.avatar || "",
        description: it.description
      }));
      const flat = roleItems.concat(otherItems);
      if (!keyword) return flat.slice(0, 16);
      const matched = flat.filter(item => String(item.name || "").toLowerCase().indexOf(keyword) !== -1);
      if (!matched.length) return flat.slice(0, 16);
      return matched.slice(0, 16);
    },
    hasGeneratedVideo() {
      return this.shotHasVideo(this.storyboard);
    },
    videoDurationSeconds() {
      return this.parseDurationSeconds(this.selectedDuration);
    },
    descriptionCharCount() {
      return this.countPromptChars(this.description);
    },
    canExpandCurrentDescription() {
      if (this.expandingDescription) return false;
      const shot = this.storyboard;
      if (!shot || !shot.id) return false;
      const txt = String(this.description || '').trim();
      if (!txt) return false;
      return this.descriptionCharCount < this.descriptionCharMin;
    },
    expandCurrentDescriptionHint() {
      if (!this.canExpandCurrentDescription) {
        if (this.expandingDescription) return '正在调用 AI 充实…';
        if (this.descriptionCharCount >= this.descriptionCharMin) return '当前描述已足够详细';
        return '请先在编辑器中输入内容';
      }
      return '调用 AI 把当前镜头扩写为 10~15s 的画面密度文本';
    },
    videoPointsPerSecond() {
      const res = this.selectedResolution || "720p";
      const m = this.currentVideoModel;
      if (m && m.pricing && typeof m.pricing === "object") {
        const p = m.pricing[res];
        if (p && typeof p.pointsPerSecond === "number" && p.pointsPerSecond > 0) {
          return p.pointsPerSecond;
        }
      }
      return 0;
    },
    generatePoints() {
      const sec = this.videoDurationSeconds;
      const rate = this.videoPointsPerSecond;
      if (!rate || rate <= 0 || !sec || sec <= 0) return 0;
      return Math.max(1, Math.round(rate * sec));
    },
    generatePointsDisplay() {
      return this.generatePoints > 0 ? String(this.generatePoints) : "—";
    },
    generateButtonText() {
      const sid = Number((this.storyboard && this.storyboard.id) || 0);
      if (this.isShotVideoGenerating(sid)) return "生成中…";
      return this.hasGeneratedVideo ? "重新生成视频" : "生成视频";
    }
  },
  watch: {
    selectedModel() {
      // 初始化回填阶段不做联动/保存，避免刷新时把数据库值误覆盖
      if (!this.step4AutoSaveReady || this.step4SyncingSelection) return;
      this.$nextTick(() => this.syncDurationResolutionSelection());
      this.schedulePersistStep4Settings();
    },
    selectedDuration() {
      if (this.step4SyncingSelection) return;
      this.schedulePersistStep4Settings();
    },
    selectedResolution() {
      if (this.step4SyncingSelection) return;
      this.schedulePersistStep4Settings();
    },
    activeEpisodeIdForStrip(n, o) {
      const a = Number(n || 0);
      const b = Number(o || 0);
      if (!(a > 0) || a === b) return;
      this.scheduleReloadEpisodeEntities();
    }
  },
  async created() {
    await Promise.all([this.loadVideoModelsFromApi(), this.loadAssetLibraryFromApi(), this.loadProjectEntitiesFromSession()]);
    await this.loadProjectMetaDraft();
    await this.loadEpisodeEntitiesFromApi();
    this.applyDurationFromStoryboard(this.storyboard);
    this.applyResolutionFromStoryboard(this.storyboard);
    this.refreshChosenAssetsFromPrompt();
    this.step4AutoSaveReady = true;
  },
  beforeDestroy() {
    if (this.settingsSaveTimer) {
      clearTimeout(this.settingsSaveTimer);
      this.settingsSaveTimer = null;
    }
    if (this._shotPromptSessionTimer) {
      clearTimeout(this._shotPromptSessionTimer);
      this._shotPromptSessionTimer = null;
    }
    if (this._promptBlurTimer) {
      clearTimeout(this._promptBlurTimer);
      this._promptBlurTimer = null;
    }
    this._activeVideoGenPolls = {};
    if (this._batchVideoPollTimer) {
      clearTimeout(this._batchVideoPollTimer);
      this._batchVideoPollTimer = null;
    }
    if (this._expandProgressTimer) {
      clearTimeout(this._expandProgressTimer);
      this._expandProgressTimer = null;
    }
    if (this._pendingStoryboardsCacheTimer) {
      const cb = this._pendingStoryboardsCacheTimer;
      if (typeof window !== 'undefined' && typeof window.cancelIdleCallback === 'function') {
        try { window.cancelIdleCallback(cb); } catch (e) { /* ignore */ }
      } else {
        clearTimeout(cb);
      }
      this._pendingStoryboardsCacheTimer = null;
    }
    this._pendingStoryboardsCacheWrite = null;
    if (typeof document !== 'undefined' && this._step4VisibilityHandler) {
      document.removeEventListener('visibilitychange', this._step4VisibilityHandler);
      this._step4VisibilityHandler = null;
    }
  },
  mounted() {
    if (this.$refs.promptEditor) {
      this.$refs.promptEditor.innerText = this.description || "";
    }
    this.hydrateSkippedShots();
    this.hydrateNextEpisode();
    // 异步从 IDB 预热 storyboards 缓存到 sessionStorage：避免 5MB 配额上限 / 减少 hydrate 主线程 JSON.parse 时间
    // 不阻塞首屏，hydrate 本身仍走同步 sessionStorage / API 路径
    try {
      const keys = storyboardsStorageKeys(this.projectId);
      if (Array.isArray(keys) && keys.length && kvStore.isAvailable()) {
        void this.warmStoryboardCachesFromKv(keys);
      }
    } catch (e) { /* ignore */ }
    // 步骤四后台批量轮询 visibility 监听：回前台时立即补一次
    if (typeof document !== 'undefined') {
      this._step4VisibilityHandler = () => {
        if (!document.hidden) {
          if (this.videoGenByShotId && Object.keys(this.videoGenByShotId).length) {
            this.ensureStep4BatchPoll();
            void this.tickStep4BatchPoll();
          }
        }
      };
      document.addEventListener('visibilitychange', this._step4VisibilityHandler);
    }
  },
  methods: {
    async loadProjectMetaDraft() {
      const id = Number(this.projectId || 0);
      if (!id) return;
      try {
        const detail = await userApi.projectDetail(id);
        this.projectSceneType = String((detail && (detail.type || detail.projectType)) || "").trim();
        this.visualStyleLabel = String((detail && detail.visualStyleLabel) || "").trim();
        this.shotPromptStyleLine = String((detail && detail.shotPromptStyleLine) || "").trim();
        if (!this.shotPromptStyleLine && this.visualStyleLabel) {
          this.shotPromptStyleLine = `画面风格:【${this.visualStyleLabel}】，4k超清画质，流畅运镜，电影级光影效果`;
        }
        this.hydrateShotsFromProjectDetail(detail);
        this.hydrateProjectEntitiesFromDetail(detail);
        const meta = detail && detail.meta && typeof detail.meta === "object" ? detail.meta : {};
        this.projectMetaDraft = meta;
        const step4 = meta && meta.step4 && typeof meta.step4 === "object" ? meta.step4 : {};
        this.hasSavedStep4Selection = !!(
          String(step4.modelCode || "").trim() ||
          String(step4.duration || "").trim() ||
          String(step4.resolution || "").trim()
        );
        if (step4.modelCode) this.selectedModel = String(step4.modelCode);
        const activeShot =
          this.storyboard && Number(this.storyboard.id || 0) > 0
            ? this.storyboard
            : Array.isArray(this.timelineItems) && this.timelineItems.length
              ? this.timelineItems[0]
              : null;
        const shotDurSec = activeShot ? this.shotPlanDurationSeconds(activeShot) : 0;
        const hasShotDuration = shotDurSec > 0;
        const hasShotResolution = !!String((activeShot && activeShot.resolution) || "").trim();
        if (!hasShotDuration && step4.duration && this.parseDurationSeconds(step4.duration) > 0) {
          this.selectedDuration = this.snapDurationToModelOptions(this.parseDurationSeconds(step4.duration));
        }
        if (!hasShotResolution && step4.resolution) {
          this.selectedResolution = this.snapResolutionToModelOptions(String(step4.resolution));
        }
      } catch (e) {
        // ignore
      } finally {
        this.syncSelectedModelWithBackendList();
        this.applyDurationFromStoryboard(this.storyboard);
        this.applyResolutionFromStoryboard(this.storyboard);
      }
    },
    hydrateProjectEntitiesFromDetail(detail) {
      if (!detail || typeof detail !== "object") return;
      const roles = Array.isArray(detail.roles) ? detail.roles : [];
      const scenes = Array.isArray(detail.scenes) ? detail.scenes : [];
      const materials = Array.isArray(detail.materials) ? detail.materials : [];
      const mappedRoles = roles.map(r => ({
        id: `d-role-${r.id}`,
        type: "role",
        name: r.name || "",
        cover: this.mediaUrl(r.avatar || r.preview || ""),
        summary: r.summary || "",
        description: r.description || ""
      }));
      const mappedScenes = scenes.map(r => ({
        id: `d-scene-${r.id}`,
        type: "scene",
        name: r.name || "",
        cover: this.mediaUrl(r.avatar || r.preview || ""),
        summary: r.summary || "",
        description: r.description || ""
      }));
      const mappedMaterials = materials
        .filter(r => r && Number((r && r.id) || 0) > 0)
        .map(r => ({
          id: `d-material-${r.id}`,
          type: "material",
          name: r.name || "",
          cover: this.mediaUrl(r.avatar || r.preview || ""),
          summary: r.summary || "",
          description: r.description || ""
        }));
      const merged = [...mappedRoles, ...mappedScenes, ...mappedMaterials].filter(x =>
        String(x.name || "").trim()
      );
      if (!merged.length) return;
      const byKey = new Map();
      merged.forEach(x => byKey.set(`${x.type}:${x.name}`, x));
      this.projectEntityLibrary = Array.from(byKey.values());
    },
    /** 将秒数对齐到当前视频模型支持的时长档位（取最接近值，如 14s → Grok 的 16s） */
    snapDurationToModelOptions(sec) {
      const targetSec = Math.max(1, Math.round(Number(sec) || 0));
      if (!targetSec) return "";
      const dOpts = this.durationOptions || [];
      if (!dOpts.length) return `${targetSec}s`;
      const exact = `${targetSec}s`;
      if (dOpts.indexOf(exact) !== -1) return exact;
      const opts = dOpts
        .map(v => ({ v, s: this.parseDurationSeconds(v) }))
        .filter(o => o.s > 0);
      if (!opts.length) return `${targetSec}s`;
      opts.sort((a, b) => Math.abs(a.s - targetSec) - Math.abs(b.s - targetSec));
      return opts[0].v;
    },
    applyDurationFromStoryboard(shot) {
      const row = shot || this.storyboard;
      if (!row) return;
      const sec = this.shotPlanDurationSeconds(row);
      if (!sec || sec <= 0) return;
      const snapped = this.snapDurationToModelOptions(sec);
      if (snapped) this.selectedDuration = snapped;
    },
    /** 对齐当前视频模型支持的分辨率；无分镜数据或不在列表内时取第一项，避免 select 显示空白 */
    snapResolutionToModelOptions(res) {
      const raw = String(res || "").trim();
      const rOpts = this.resolutionOptions || [];
      if (!rOpts.length) return raw || "720p";
      if (raw && rOpts.indexOf(raw) !== -1) return raw;
      return rOpts[0];
    },
    applyResolutionFromStoryboard(shot) {
      const row = shot || this.storyboard;
      const raw = row ? String(row.resolution || "").trim() : "";
      this.selectedResolution = this.snapResolutionToModelOptions(raw);
    },
    hydrateShotsFromProjectDetail(detail) {
      this.step4TimelineHydrated = false;
      if (!detail || typeof detail !== "object") return;
      const requestedId = Number((this.episode && this.episode.id) || this.$route.query.id || 0);
      const episodes = Array.isArray(detail.episodes) ? detail.episodes : [];
      let hit = episodes.find(ep => Number((ep && ep.id) || 0) === requestedId);
      /** 路由 ?id= 误填为分镜主键（fa_ai_project_shot.id）时，按 shots 反查所属分集 */
      let routeIdWasShotPk = 0;
      if (!hit && requestedId > 0) {
        for (const ep of episodes) {
          const sh = Array.isArray(ep.shots) ? ep.shots : [];
          if (sh.some(s => Number((s && s.id) || 0) === requestedId)) {
            hit = ep;
            routeIdWasShotPk = requestedId;
            break;
          }
        }
      }
      let usedEpisodeFallback = false;
      /** 当前路由分集存在但 shots 已迁空/未生成：改挂到「首条有镜头的分集」，避免仍沿用 sessionStorage 里的旧 fa_ai_project_shot.id 调 save → 404 */
      let switchedEpisodeForShots = false;
      // 路由里的分集 id 可能已是旧书签或其它项目缓存（当前项目下不存在该 episode），继续沿用会带上错误的 shot 主键
      if (!hit && episodes.length) {
        hit = episodes[0];
        usedEpisodeFallback = true;
      }
      if (!hit) return;
      let shots = Array.isArray(hit.shots) ? hit.shots : [];
      if (!shots.length) {
        const alt =
          episodes.find(
            ep =>
              Number((ep && ep.id) || 0) !== Number((hit && hit.id) || 0) &&
              Array.isArray(ep.shots) &&
              ep.shots.length > 0
          ) || episodes.find(ep => Array.isArray(ep.shots) && ep.shots.length > 0);
        if (alt) {
          hit = alt;
          switchedEpisodeForShots = true;
          shots = Array.isArray(hit.shots) ? hit.shots : [];
        }
      }
      // 分集 id 与路由对齐后再处理时间轴（含：无镜头分集切到有镜头分集）
      if ((usedEpisodeFallback || routeIdWasShotPk > 0 || switchedEpisodeForShots) && this.$router) {
        const nextEpId = String(hit.id);
        if (String(this.$route.query.id || "") !== nextEpId) {
          this.$router.replace({ path: this.$route.path, query: { ...this.$route.query, id: nextEpId } }).catch(() => {});
        }
      }
      this.episode = {
        ...this.episode,
        id: Number(hit.id),
        no: Number(hit.no || hit.episode_no || hit.episodeNo || 0),
        title: String(hit.title || this.episode.title || ""),
        cover: hit.cover || this.episode.cover || "",
        duration: hit.duration || this.episode.duration || "",
        summary: hit.summary || this.episode.summary || ""
      };
      if (!shots.length) {
        this.timelineItems = [];
        this.storyboard = {
          id: 0,
          shotId: 0,
          shotNo: 0,
          title: "暂无分镜",
          cover: "",
          videoUrl: "",
          videoCoverUrl: "",
          durationSeconds: 5,
          durationText: "5s",
          resolution: "",
          prompt: ""
        };
        this.description = "";
        this.previewVideoUrl = "";
        this.step4TimelineHydrated = false;
        this.scheduleReloadEpisodeEntities();
        return;
      }
      const mapped = shots.map((s, idx) => ({
        id: Number((s && s.id) || 0),
        shotId: 0,
        shotNo: Number((s && (s.no || s.shotNo)) || idx + 1),
        title: s.title || `画面 ${idx + 1}`,
        // 仅保留镜头自有封面（接口一般不返回）；不用分集 cover，避免步骤三分集回填后在步骤四误显示「已有图」
        cover: String((s && s.cover) || "").trim(),
        videoUrl: this.mediaUrl((s && (s.videoUrl || s.video_url)) || ""),
        videoCoverUrl: this.mediaUrl((s && (s.videoCoverUrl || s.video_cover_url)) || ""),
        durationSeconds: typeof s.durationSeconds === "number" ? s.durationSeconds : 5,
        durationText: `${typeof s.durationSeconds === "number" ? s.durationSeconds : 5}s`,
        resolution: String((s && s.resolution) || "").trim(),
        prompt: s.prompt || s.description || ""
      }));
      this.timelineItems = mapped;
      const pickByPk =
        routeIdWasShotPk > 0 ? mapped.find(x => Number(x.id) === Number(routeIdWasShotPk)) : null;
      this.storyboard = pickByPk || mapped[0] || this.storyboard;
      this.previewVideoUrl = this.mediaUrl((this.storyboard && this.storyboard.videoUrl) || "");
      this.description = this.normalizePromptForDisplay(String((this.storyboard && this.storyboard.prompt) || ""));
      // storyboard.id 变化会触发 promptEditor 的 :key 换节点；同步阶段 ref 仍指向将被销毁的旧 DOM，
      // 必须在 DOM 更新后再写入，否则首条分镜刷新后输入框会一直为空（点其它分镜时 nextTick 会再写一次故正常）。
      this.$nextTick(() => {
        const el = this.$refs.promptEditor;
        if (el) el.innerText = this.description || "";
        this.autoTagMentionsInEditor();
      });
      // 同步刷新本地缓存延后到 idle 时段（见 schedulePersistStoryboardsCacheIdle）；当前方法内
      // 不直接写 sessionStorage，避免阻塞 hydrate 主线程 30~80ms（packed JSON.stringify 数量级）。
      // 防御：分集 id 已变更后立刻标记，待 schedule 在 idle 时段真正落盘。
      this._pendingStoryboardsCacheWrite = {
        episodeId: Number(hit.id),
        mappedShallow: mapped.map(x => ({ ...x }))
      };
      this.step4TimelineHydrated = true;
      this.scheduleReloadEpisodeEntities();
      if (this.storyboard && Number(this.storyboard.id || 0) > 0) {
        this.applyDurationFromStoryboard(this.storyboard);
        this.applyResolutionFromStoryboard(this.storyboard);
      }
      this.resumeStep4VideoTasks();
      // 分集 shots 回填完成后，sessionStorage 同步写入拆到 idle 时段：
      //   - JSON.stringify 在 storyboards 字段可能含上百条 shots，主线程会卡 30~80ms
      //   - 用 requestIdleCallback + setTimeout 兜底，确保低端机 hydrate 不卡首屏
      this.schedulePersistStoryboardsCacheIdle(hit.id, mapped);
    },
    buildStep4SettingsMeta() {
      const base = this.projectMetaDraft && typeof this.projectMetaDraft === "object" ? this.projectMetaDraft : {};
      return {
        ...base,
        step4: {
          ...(base.step4 && typeof base.step4 === "object" ? base.step4 : {}),
          modelCode: String(this.selectedModel || ""),
          duration: String(this.selectedDuration || ""),
          resolution: String(this.selectedResolution || "")
        }
      };
    },
    schedulePersistStep4Settings() {
      const id = Number(this.projectId || 0);
      if (!id) return;
      if (!this.step4AutoSaveReady) return;
      if (this.settingsSaveTimer) {
        clearTimeout(this.settingsSaveTimer);
      }
      this.settingsSaveTimer = setTimeout(() => {
        this.persistStep4SettingsNow();
      }, 400);
    },
    async persistStep4SettingsNow() {
      const id = Number(this.projectId || 0);
      if (!id) return;
      const nextMeta = this.buildStep4SettingsMeta();
      this.projectMetaDraft = nextMeta;
      try {
        const currentShotId = this.resolveShotId(this.storyboard);
        const step4Ctx = this.buildStep4PersistContext(this.storyboard);
        const canSaveShotRow =
          this.step4TimelineHydrated &&
          (currentShotId > 0 ||
            (step4Ctx.episodeId > 0 && step4Ctx.shotNo > 0) ||
            (step4Ctx.episodeNo > 0 && step4Ctx.shotNo > 0));
        const cfgPromise = canSaveShotRow
          ? userApi.saveStoryboardVideoConfig({
              projectId: id,
              episodeId: step4Ctx.episodeId,
              episodeNo: step4Ctx.episodeNo,
              shotNo: step4Ctx.shotNo,
              storyboardId: Number(currentShotId),
              shotId: Number(currentShotId),
              duration: String(this.selectedDuration || ""),
              resolution: String(this.selectedResolution || "")
            })
          : Promise.resolve(null);
        const metaPromise = userApi.updateProjectMetaJson({
          id,
          step4ModelCode: String(this.selectedModel || ""),
          step4Duration: String(this.selectedDuration || ""),
          step4Resolution: String(this.selectedResolution || "")
        });
        const [cfg] = await Promise.all([cfgPromise, metaPromise]);
        if (cfg && typeof cfg === "object") {
          const sec = Number(cfg.durationSeconds || 0);
          const resolvedPk = Number(cfg.shotId || 0);
          // 只有明确收到非空 prompt 才更新（后端返回 null 时表示本次未修改 prompt）
          const savedPrompt = cfg.prompt != null ? String(cfg.prompt).trim() : "";
          const idx = (this.timelineItems || []).findIndex(it => {
            const pk = Number(it && it.id);
            const no = Number(it && it.shotNo);
            return (
              (currentShotId > 0 && pk === Number(currentShotId)) ||
              (step4Ctx.shotNo > 0 && no === step4Ctx.shotNo) ||
              (resolvedPk > 0 && pk === resolvedPk)
            );
          });
          if (sec > 0) {
            const nextDurationText = this.snapDurationToModelOptions(sec) || `${Math.round(sec)}s`;
            this.selectedDuration = nextDurationText;
            const durSec = this.parseDurationSeconds(nextDurationText);
            if (idx !== -1) {
              const patch = {
                ...this.timelineItems[idx],
                ...(resolvedPk > 0 ? { id: resolvedPk } : {}),
                durationSeconds: durSec,
                durationText: nextDurationText,
                resolution: String(cfg.resolution || this.selectedResolution || "")
              };
              // 只有收到非空 prompt 且与当前编辑内容不同时才更新（避免覆盖用户正在编辑的内容）
              if (savedPrompt && savedPrompt !== this.description) {
                patch.prompt = savedPrompt;
              }
              this.$set(this.timelineItems, idx, patch);
              const selNo = Number(this.storyboard && this.storyboard.shotNo);
              if (selNo === Number(this.timelineItems[idx].shotNo)) {
                this.storyboard = this.timelineItems[idx];
                // 只有收到非空 prompt 且与当前编辑内容不同时才更新编辑器
                if (savedPrompt && savedPrompt !== this.description) {
                  const displayPrompt = this.normalizePromptForDisplay(savedPrompt);
                  this.description = displayPrompt;
                  this.$nextTick(() => {
                    if (this.$refs.promptEditor) {
                      this.$refs.promptEditor.innerText = displayPrompt || "";
                    }
                    this.autoTagMentionsInEditor();
                  });
                }
              }
            }
          }
          if (cfg.resolution) {
            this.selectedResolution = String(cfg.resolution);
          }
        }
      } catch (e) {
        // 自动保存失败不打断主流程
      }
    },
    /** episodeId + episodeNo + shot_no，供步骤四接口在主键/分集 id 失效时兜底解析 fa_ai_project_shot */
    buildStep4PersistContext(item) {
      const it = item || this.storyboard;
      const episodeId = this.resolveActiveEpisodeId();
      const episodeNo = Number(
        (this.episode && (this.episode.no || this.episode.episodeNo)) || 0
      );
      const shotNo = Number((it && it.shotNo) || 0);
      return { episodeId, episodeNo, shotNo };
    },
    /**
     * 当前分集 id：始终以 this.episode 为准（由 projectDetail hydrate 或缓存对齐），再回退路由。
     * 避免「已纠正分集 id、但 step4TimelineHydrated 仍为 false（如无分镜行提前 return）」时仍用旧 query.id 调接口导致 404。
     */
    resolveActiveEpisodeId() {
      const ep = Number((this.episode && this.episode.id) || 0);
      if (ep > 0) return ep;
      const q = Number(this.$route.query.id || 0);
      return q > 0 ? q : 0;
    },
    scheduleReloadEpisodeEntities() {
      this.$nextTick(() => {
        void this.loadEpisodeEntitiesFromApi();
      });
    },
    /**
     * 把 hydrate 阶段的 storyboards 缓存写入延后到浏览器 idle 时段，避免阻塞主线程首屏 30~80ms。
     * 多次连续 hydrate / 多次切换分集时，合并成一次写入（同一 _pendingStoryboardsCacheWrite 单例）。
     * 写入走 IDB 异步（降级 sessionStorage），单条 cache 大小可超 5MB 不再炸 sessionStorage 配额。
     * 兜底：requestIdleCallback 不支持时用 setTimeout(0)。
     */
    schedulePersistStoryboardsCacheIdle(episodeId, mapped) {
      if (!episodeId || !Array.isArray(mapped)) return;
      this._pendingStoryboardsCacheWrite = {
        episodeId: Number(episodeId),
        mappedShallow: mapped.map(x => ({ ...x }))
      };
      if (this._pendingStoryboardsCacheTimer) return;
      const flush = () => {
        this._pendingStoryboardsCacheTimer = null;
        const pending = this._pendingStoryboardsCacheWrite;
        this._pendingStoryboardsCacheWrite = null;
        if (!pending) return;
        try {
          const packs = this.readStoryboardCaches();
          packs.forEach(pack => {
            const nextList = (pack.list || []).map(ep => {
              if (Number((ep && ep.id) || 0) !== Number(pending.episodeId)) return ep;
              return { ...ep, shots: pending.mappedShallow.map(x => ({ ...x })) };
            });
            // 走 IDB 异步写入（断网 / 配额满时降级 sessionStorage）
            // 写完会自然触发下次 hydrate 复用，避免 sessionStorage 5MB 上限被 step4 大列表撑爆
            kvStore.set(
              pack.key,
              nextList,
              (serialized) => { try { sessionStorage.setItem(pack.key, serialized); } catch (e) { /* ignore */ } }
            );
          });
        } catch (e) {
          /* ignore: sessionStorage 写失败不阻塞 UI */
        }
      };
      if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
        this._pendingStoryboardsCacheTimer = window.requestIdleCallback(flush, { timeout: 800 });
      } else {
        this._pendingStoryboardsCacheTimer = setTimeout(flush, 0);
      }
    },
    /**
     * 一次性从 IDB 同步最近一次成功的 storyboards 缓存到 sessionStorage（hydrate 阶段使用）。
     * IDB 不可用 / key 不存在 → 静默返回，hydrate 继续走 sessionStorage / server 路径。
     */
    async warmStoryboardCachesFromKv(keys) {
      if (!Array.isArray(keys) || !keys.length) return;
      if (!kvStore.isAvailable()) return;
      for (const key of keys) {
        try {
          const cached = await kvStore.get(key, () => {
            try { return sessionStorage.getItem(key); } catch (e) { return null; }
          });
          if (Array.isArray(cached) && cached.length) {
            try { sessionStorage.setItem(key, JSON.stringify(cached)); } catch (e) { /* ignore */ }
          }
        } catch (e) {
          /* ignore: 单个 key 失败不影响其它 */
        }
      }
    },
    resolveShotId(item) {
      if (!item) return 0;
      const pk = Number(item.id || 0);
      if (pk > 0) return pk;
      const sid = Number(item.shotId || 0);
      return sid > 0 ? sid : 0;
    },
    syncCurrentStoryboardPrompt(immediateSession = false) {
      const currentId = Number((this.storyboard && this.storyboard.id) || 0);
      if (currentId <= 0) return;
      const editor = this.$refs.promptEditor;
      const raw = editor ? String(editor.innerText || "") : String(this.description || "");
      const normalized = this.normalizePromptForBackend(raw);
      this.description = raw;
      this.storyboard.prompt = normalized;
      const list = Array.isArray(this.timelineItems) ? this.timelineItems : [];
      const idx = list.findIndex(it => Number(it && it.id) === currentId);
      if (idx !== -1) {
        this.$set(this.timelineItems, idx, {
          ...list[idx],
          prompt: normalized
        });
      }
      if (immediateSession) {
        if (this._shotPromptSessionTimer) {
          clearTimeout(this._shotPromptSessionTimer);
          this._shotPromptSessionTimer = null;
        }
        this.updateShotPromptInSessionCache(currentId, normalized);
      } else {
        this.scheduleShotPromptSessionCache(currentId, normalized);
      }
    },
    scheduleShotPromptSessionCache(shotId, prompt) {
      if (this._shotPromptSessionTimer) {
        clearTimeout(this._shotPromptSessionTimer);
      }
      const sid = Number(shotId || 0);
      const val = String(prompt || "");
      this._shotPromptSessionTimer = setTimeout(() => {
        this._shotPromptSessionTimer = null;
        this.updateShotPromptInSessionCache(sid, val);
      }, 280);
    },
    notifyPointsChanged() {
      if (this.$root && typeof this.$root.$emit === "function") {
        this.$root.$emit("points-changed");
      }
    },
    onImgError(item, field) {
      if (!item || !field) return;
      try {
        item[field] = "";
      } catch (e) {
        /* ignore */
      }
    },
    normalizePromptForDisplay(text) {
      const expanded = this.expandAtImagePlaceholdersInPrompt(String(text || ""));
      let s = stripShotPromptEpisodeHeaderForPrompt(stripShotPromptStyleLineForPrompt(stripVideoLanguageLineForPrompt(expanded)));
      // 段落分隔：连续 ≥2 个换行折叠为单个，避免编辑框渲染出多余的空行 / 多余 <br>
      s = s.replace(/\r\n/g, "\n").replace(/\n{2,}/g, "\n");
      // 对话行专用优化：「角色（神情）：<任意空白换行>\"台词\"」折叠为冒号同行写法，
      // 解决用户反馈的「张三说道：<br>"台词"」多 br 问题（源数据保留换行以便后续段落切分）。
      // 匹配：「中文/标点后冒号 + 换行 + 可选空白 + 双引号开头的台词」，仅替换第一个换行。
      s = s.replace(/([：:])\s*\n\s*(?=\")/g, "$1");
      const styleLine = String(this.shotPromptStyleLine || "").trim();
      if (styleLine && !/画面风格/.test(s)) {
        s = `${styleLine}\n${s}`;
      }
      return normalizeShotPromptDurationForDisplay(s);
    },
    /**
     * 与 UserApi::extractMentionNamesFromText 对齐：按正文从左到右提取 @ 后的名称（首次出现顺序，不含 @ImageN）。
     */
    extractMentionNamesPhpStyle(text) {
      const s = String(text || "").trim();
      if (!s) return [];
      const ordered = [];
      const seen = new Set();
      const re = /@([^\s@\uFF20（）()，,。！？:：/／]+)/g;
      let m;
      while ((m = re.exec(s))) {
        let n = String(m[1] || "")
          .trim()
          .replace(/[/／]+$/g, "");
        if (!n || seen.has(n)) continue;
        if (/^Image\d+$/i.test(n)) continue;
        seen.add(n);
        ordered.push(n);
      }
      return ordered;
    },
    /**
     * 与 UserApi::resolveStep4ReferencesFromEpisodeBindings 三阶段顺序尽量一致，得到 @Image1… 对应的 bindName 序列（去重后的「最终参考图顺序」），
     * 用于把落库的 @Image7 等还原为 @实体名；比仅用 assetStripAssets 更能覆盖「文本里只有 6 个 @、后面靠分集绑定补齐到 10 张」的情况。
     */
    buildStep4ResolvedRefBindNamesForImageExpand(promptText) {
      const s = String(promptText || "");
      const boundByName = new Map();
      (this.currentEpisodeEntities || []).forEach(it => {
        const name = String(it.name || "").trim();
        if (!name || boundByName.has(name)) return;
        const url = String(it.cover || "").trim();
        if (!url || this.isPlaceholderAsset(url)) return;
        boundByName.set(name, it);
      });
      const out = [];
      const picked = new Set();
      const push = n0 => {
        const n = String(n0 || "")
          .trim()
          .replace(/[/／]+$/g, "");
        if (!n || picked.has(n) || /^Image\d+$/i.test(n)) return false;
        picked.add(n);
        out.push(n);
        return true;
      };
      const byLibName = new Map(
        (this.mentionSourceLibrary || []).map(it => [String(it.name || "").trim(), it])
      );
      (this.buildReferencesFromPromptText(s, { includeStripAssets: false }) || []).forEach(r => {
        const n = String((r && r.bindName) || "")
          .trim()
          .replace(/[/／]+$/g, "");
        if (!n || picked.has(n) || /^Image\d+$/i.test(n)) return;
        const url = String((r && r.url) || "").trim();
        const hit = byLibName.get(n);
        const u2 = url || (hit && String(hit.cover || "").trim()) || "";
        if (!u2 || this.isPlaceholderAsset(u2)) return;
        push(n);
      });
      this.extractMentionNamesPhpStyle(s).forEach(n => {
        if (!boundByName.has(n) || picked.has(n)) return;
        push(n);
      });
      (this.currentEpisodeEntities || []).forEach(it => {
        const n = String(it.name || "").trim();
        if (!n || picked.has(n)) return;
        if (!it.cover || this.isPlaceholderAsset(it.cover)) return;
        push(n);
      });
      return out;
    },
    /**
     * 将历史落库/误存的 @Image1… 恢复为 @实体名，便于 @ 自动成卡。
     * 仅替换「能确定对应关系」的序号：顺序来自 buildStep4ResolvedRefBindNamesForImageExpand（与后端合并规则尽量一致）。
     * 无法对应时保留 @ImageN，绝不轮询凑槽位——否则会误把「击中@邪妖」变成「击中@林逸的公寓」等语义错误。
     */
    expandAtImagePlaceholdersInPrompt(text) {
      const s = String(text || "");
      if (!/@Image\d+/i.test(s)) return s;
      const names = this.buildStep4ResolvedRefBindNamesForImageExpand(s);
      if (!names.length) {
        const list = [];
        const seen = new Set();
        (this.assetStripAssets || []).forEach(x => {
          if (!x || !String(x.name || "").trim()) return;
          const t = this.normalizeMentionEntityType(x.type);
          const k = `${t}:${String(x.name).trim()}`;
          if (seen.has(k)) return;
          if (!x.cover || this.isPlaceholderAsset(x.cover)) return;
          seen.add(k);
          list.push(String(x.name).trim());
        });
        if (!list.length) return s;
        return s.replace(/@Image(\d+)/gi, (_, num) => {
          const i = parseInt(num, 10) - 1;
          if (i < 0 || i >= list.length) return `@Image${num}`;
          return `@${list[i]}`;
        });
      }
      return s.replace(/@Image(\d+)/gi, (_, num) => {
        const i = parseInt(num, 10) - 1;
        if (i < 0 || i >= names.length) return `@Image${num}`;
        return `@${names[i]}`;
      });
    },
    /** 分集实体条就绪后：若当前镜头 prompt 误存为 @ImageN，则换回 @实体名并写回编辑器与 timeline */
    rehydrateEditorPromptIfImagePlaceholders() {
      const row = this.storyboard;
      if (!row) return;
      const raw = String(row.prompt || "");
      if (!/@Image\d+/i.test(raw)) return;
      const expanded = this.expandAtImagePlaceholdersInPrompt(raw);
      if (expanded === raw) return;
      const persisted = this.normalizePromptForBackend(expanded);
      this.$set(row, "prompt", persisted);
      const idx = (this.timelineItems || []).findIndex(it => Number(it && it.id) === Number(row.id));
      if (idx !== -1) {
        this.$set(this.timelineItems, idx, { ...this.timelineItems[idx], prompt: persisted });
      }
      this.description = this.normalizePromptForDisplay(persisted);
      const el = this.$refs.promptEditor;
      if (el) el.innerText = this.description || "";
    },
    normalizePromptForBackend(text) {
      // 画风固定行 / 镜号段 / 「生成语言」由步骤四注入；镜号与时长是元信息，不落库
      let s = stripShotPromptEpisodeHeaderForPrompt(stripShotPromptStyleLineForPrompt(stripVideoLanguageLineForPrompt(String(text || ""))));
      s = normalizeShotPromptDurationForDisplay(s);
      if (!s) return "";
      s = s.replace(/[ \t]+/g, " ").replace(/\r\n/g, "\n").replace(/\n{2,}/g, "\n");
      // 与 normalizePromptForDisplay 对齐：对话行「角色（语气）：<换行>"台词"」折叠为同行写法
      s = s.replace(/([：:])\s*\n\s*(?=\")/g, "$1");
      return s;
    },
    ssKey(kind) {
      const id = Number(this.projectId || 0);
      if (!id) return `project_${kind}_cache`;
      return `project_${kind}_cache_${id}`;
    },
    readSessionList(kind) {
      try {
        const raw = sessionStorage.getItem(this.ssKey(kind)) || "[]";
        const arr = JSON.parse(raw);
        return Array.isArray(arr) ? arr : [];
      } catch (e) {
        return [];
      }
    },
    readStoryboardCaches() {
      const keys = storyboardsStorageKeys(this.projectId);
      return keys.map(key => {
        try {
          const arr = JSON.parse(sessionStorage.getItem(key) || "[]");
          return { key, list: Array.isArray(arr) ? arr : [] };
        } catch (e) {
          return { key, list: [] };
        }
      });
    },
    updateShotPromptInSessionCache(shotId, prompt) {
      const sid = Number(shotId || 0);
      if (sid <= 0) return;
      const value = String(prompt || "");
      const packs = this.readStoryboardCaches();
      packs.forEach(pack => {
        let changed = false;
        const nextList = (pack.list || []).map(ep => {
          const shots = Array.isArray(ep && ep.shots) ? ep.shots : [];
          let epChanged = false;
          const nextShots = shots.map(s => {
            if (Number((s && s.id) || 0) !== sid) return s;
            epChanged = true;
            changed = true;
            return {
              ...s,
              prompt: value,
              description: value
            };
          });
          if (!epChanged) return ep;
          return {
            ...ep,
            shots: nextShots
          };
        });
        if (changed) {
          sessionStorage.setItem(pack.key, JSON.stringify(nextList));
        }
      });
    },
    loadProjectEntitiesFromSession() {
      const roles = this.readSessionList("roles").map(r => ({
        id: `p-role-${r.id}`,
        type: "role",
        name: r.name || "",
        cover: this.mediaUrl(r.avatar || r.preview || ""),
        summary: r.summary || "",
        description: r.description || ""
      }));
      const scenes = this.readSessionList("scenes").map(r => ({
        id: `p-scene-${r.id}`,
        type: "scene",
        name: r.name || "",
        cover: this.mediaUrl(r.avatar || r.preview || ""),
        summary: r.summary || "",
        description: r.description || ""
      }));
      const materials = this.readSessionList("materials").map(r => ({
        id: `p-material-${r.id}`,
        type: "material",
        name: r.name || "",
        cover: this.mediaUrl(r.avatar || r.preview || ""),
        summary: r.summary || "",
        description: r.description || ""
      }));
      const legacyProducts = this.readSessionList("products").map(r => ({
        id: `p-material-${r.id}`,
        type: "material",
        name: r.name || "",
        cover: this.mediaUrl(r.avatar || r.preview || ""),
        summary: r.summary || "",
        description: r.description || ""
      }));
      const merged = [...roles, ...scenes, ...materials, ...legacyProducts].filter(x => String(x.name || "").trim());
      const byKey = new Map();
      merged.forEach(x => byKey.set(`${x.type}:${x.name}`, x));
      this.projectEntityLibrary = Array.from(byKey.values());
    },
    async loadEpisodeEntitiesFromApi() {
      const token = localStorage.getItem("user_token");
      const projectId = Number(this.projectId || 0);
      const episodeId = this.resolveActiveEpisodeId();
      if (!token || !projectId || !episodeId) {
        this.episodeStripHydrated = true;
        this.$nextTick(() => {
          this.autoTagMentionsInEditor();
        });
        return;
      }
      try {
        const res = await userApi.listProjectStoryboards({
          id: projectId,
          episodeId
        });
        const list = (res && res.storyboards) || [];
        const hit = Array.isArray(list) ? list.find(it => Number(it.id) === episodeId) : null;
        const entities = (hit && Array.isArray(hit.entities)) ? hit.entities : [];
        this.currentEpisodeEntities = entities
          .map(it => {
            let t = this.normalizeMentionEntityType(it.type);
            return {
              id: `ep-${it.id}`,
              type: t,
              name: it.name || "",
              cover: this.mediaUrl(it.cover || ""),
              bindSource: String(it.bindSource || it.bind_source || "")
            };
          })
          .filter(it => String(it.name || "").trim());
      } catch (e) {
        this.currentEpisodeEntities = [];
      } finally {
        this.episodeStripHydrated = true;
        // 分集绑定主体异步返回后重跑 @ → 标签，避免首屏 innerText 写入时实体表仍为空
        this.$nextTick(() => {
          this.rehydrateEditorPromptIfImagePlaceholders();
          this.autoTagMentionsInEditor();
        });
      }
    },
    maxVideoDurationSecondsForUi() {
      const opts = this.durationOptions || [];
      if (!opts.length) return 15;
      const secs = opts.map(v => this.parseDurationSeconds(v)).filter(s => s > 0);
      return secs.length ? Math.max(...secs) : 15;
    },
    clampDurationSeconds(n) {
      const v = Number(n);
      if (!Number.isFinite(v) || v <= 0) return 5;
      const max = this.maxVideoDurationSecondsForUi();
      return Math.max(1, Math.min(max, Math.round(v)));
    },
    /** 分镜条上展示的时长（秒），用于时间轴统计 */
    shotPlanDurationSeconds(item) {
      if (!item) return 0;
      const n = Number(item.durationSeconds);
      if (Number.isFinite(n) && n > 0) return this.clampDurationSeconds(n);
      return this.clampDurationSeconds(this.parseDurationSeconds(item.durationText));
    },
    formatToolbarMmSs(sec) {
      const s = Math.max(0, Math.floor(Number(sec) || 0));
      const m = Math.floor(s / 60);
      const r = s % 60;
      return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
    },
    /** 与后端 videoModels 列表对齐当前选中模型；无数据时清空 */
    syncSelectedModelWithBackendList() {
      const list = Array.isArray(this.videoModelsRaw) ? this.videoModelsRaw : [];
      const codes = new Set(list.map(m => String((m && m.code) || "")));
      if (!list.length) {
        this.selectedModel = "";
        return;
      }
      const cur = String(this.selectedModel || "");
      if (!cur || !codes.has(cur)) {
        this.selectedModel = String((list[0] && list[0].code) || "");
      }
      this.$nextTick(() => this.syncDurationResolutionSelection());
    },
    recalcTotalDurationAndBind(allowAutoBind = true) {
      if (!allowAutoBind) return;
      const sum = (this.timelineItems || []).reduce((acc, it) => {
        const sec = this.clampDurationSeconds(it.durationSeconds || this.parseDurationSeconds(it.durationText));
        return acc + sec;
      }, 0);
      const max = this.maxVideoDurationSecondsForUi();
      const total = Math.max(1, Math.min(max, sum || 0));
      const snapped = this.snapDurationToModelOptions(total);
      if (snapped) this.selectedDuration = snapped;
    },
    async loadVideoModelsFromApi() {
      try {
        const list = await userApi.videoModels();
        this.videoModelsRaw = Array.isArray(list) ? list : [];
      } catch (e) {
        this.videoModelsRaw = [];
      }
      this.syncSelectedModelWithBackendList();
    },
    syncDurationResolutionSelection() {
      const dOpts = this.durationOptions;
      const rOpts = this.resolutionOptions;
      if (dOpts.length) {
        const curSec = this.parseDurationSeconds(this.selectedDuration);
        const snapped = this.snapDurationToModelOptions(curSec);
        if (snapped) this.selectedDuration = snapped;
      }
      if (rOpts.length) {
        this.selectedResolution = this.snapResolutionToModelOptions(this.selectedResolution);
      }
    },
    async loadAssetLibraryFromApi() {
      const token = localStorage.getItem("user_token");
      if (!token) return;
      try {
        const data = await userApi.assets();
        const images = (data && data.image) || [];
        const apiAssets = images.map(item => ({
          id: item.id,
          type: item.assetType || "material",
          name: item.name || "",
          cover: this.mediaUrl(item.cover || item.url),
          volcAssetId: String(item.volcAssetId || "").trim(),
          volcAssetStatus: String(item.volcAssetStatus || "").trim()
        }));
        this.assetLibrary = apiAssets.filter(x => String(x.name || "").trim());
      } catch (e) {
        // ignore
      }
    },
    mediaUrl(src) {
      const s = String(src || "").trim();
      if (!s) return "";
      if (/^https?:\/\//i.test(s)) return s;
      return s.startsWith("/") ? s : `/${s}`;
    },
    isPlaceholderAsset(src) {
      const s = String(src || "").trim();
      if (!s) return true;
      return /dummyimage\.com/i.test(s);
    },
    formatModelLabel(model) {
      const raw = Array.isArray(this.videoModelsRaw) ? this.videoModelsRaw : [];
      const codeRow = raw.find(m => m.code === model || m.name === model);
      if (codeRow && (codeRow.name || codeRow.code)) return String(codeRow.name || codeRow.code);
      return String(model || "—");
    },
    parseDurationSeconds(value) {
      const m = String(value || "").match(/^(\d+(?:\.\d+)?)\s*s$/i);
      if (!m) return 9;
      const n = parseFloat(m[1]);
      return Number.isFinite(n) && n > 0 ? n : 9;
    },
    goBackToStoryboardList() {
      // 返回应回到第三步“分集脚本”列表页（而不是简单 history.back）
      this.$router.push({
        path: "/project/setup",
        query: {
          id: this.projectId || undefined,
          step: 3
        }
      });
    },
    isStoryboardAssetModalType(t) {
      return ["role", "scene", "material"].indexOf(t) !== -1;
    },
    appendPromptTemplate(text) {
      const block = String(text || "").trim();
      if (!block) return;
      const ed = this.$refs.promptEditor;
      if (!ed) return;
      const cur = String(ed.innerText || "").trim();
      ed.innerText = cur ? `${cur}\n${block}` : block;
      this.description = ed.innerText || "";
      this.syncCurrentStoryboardPrompt(true);
    },
    openAssetModal() {
      const draft = { role: [], scene: [], material: [] };
      const seen = { role: new Set(), scene: new Set(), material: new Set() };
      const userLib = this.assetLibrary || [];
      const userKey = new Set(userLib.map(x => `${x.type}:${String(x.id)}`));
      (this.taskAssetLibrary || []).forEach(it => {
        const t = this.isStoryboardAssetModalType(it.type) ? it.type : "material";
        const k = `${t}:${String(it.id)}`;
        if (!userKey.has(k)) return;
        const sid = String(it.id);
        if (seen[t].has(sid)) return;
        seen[t].add(sid);
        draft[t].push(it.id);
      });
      this.modalAssetSelectionDraft = draft;
      this.tempSelectedAssetIds = [...(draft[this.activeAssetType] || [])];
      this.assetModalVisible = true;
    },
    switchAssetModalTab(nextType) {
      if (nextType === this.activeAssetType) return;
      const cur = this.isStoryboardAssetModalType(this.activeAssetType) ? this.activeAssetType : "material";
      this.modalAssetSelectionDraft[cur] = [...this.tempSelectedAssetIds];
      this.activeAssetType = nextType;
      this.tempSelectedAssetIds = [...(this.modalAssetSelectionDraft[nextType] || [])];
    },
    closeAssetModal() {
      this.assetModalVisible = false;
      this.tempSelectedAssetIds = [];
      this.modalAssetSelectionDraft = { role: [], scene: [], material: [] };
    },
    isTempAssetSelected(id) {
      const sid = String(id);
      return (this.tempSelectedAssetIds || []).some(x => String(x) === sid);
    },
    toggleTempAsset(id) {
      const sid = String(id);
      const list = this.tempSelectedAssetIds || [];
      const index = list.findIndex(x => String(x) === sid);
      if (index === -1) {
        this.tempSelectedAssetIds = [...list, id];
      } else {
        const next = [...list];
        next.splice(index, 1);
        this.tempSelectedAssetIds = next;
      }
    },
    confirmAssets() {
      const cur = this.isStoryboardAssetModalType(this.activeAssetType) ? this.activeAssetType : "material";
      const draft = { ...this.modalAssetSelectionDraft, [cur]: [...this.tempSelectedAssetIds] };
      const userLib = this.assetLibrary || [];
      const userByKey = new Map(userLib.map(x => [`${x.type}:${String(x.id)}`, x]));
      const nextTask = [];
      ["role", "scene", "material"].forEach(t => {
        const ids = draft[t] || [];
        ids.forEach(rawId => {
          const key = `${t}:${String(rawId)}`;
          const item = userByKey.get(key);
          if (!item) return;
          const normalized = {
            ...item,
            type: item.type || t,
            name: String(item.name || "").trim()
          };
          if (normalized.name) nextTask.push(normalized);
        });
      });
      this.taskAssetLibrary = nextTask;
      this.assetModalVisible = false;
      this.tempSelectedAssetIds = [];
      this.modalAssetSelectionDraft = { role: [], scene: [], material: [] };
      void this.persistMixedEpisodeBindings(nextTask, false);
    },
    uploadLocal() {
      const input = this.$refs.localUploadInput;
      if (input) input.click();
    },
    async onLocalUploadChange(event) {
      const input = event && event.target;
      const file = input && input.files && input.files[0] ? input.files[0] : null;
      if (!file) return;
      const token = localStorage.getItem("user_token");
      try {
        let item = null;
        if (token) {
          const r = await userApi.uploadAsset({
            file,
            assetType: this.activeAssetType || "material",
            name: file.name
          });
          const id = r && r.id ? r.id : `local-${Date.now()}`;
          item = {
            id,
            type: this.activeAssetType || "material",
            name: String((r && r.name) || file.name || `本地素材${Date.now()}`),
            cover: this.mediaUrl((r && (r.cover || r.url || r.fileUrl)) || "")
          };
        } else {
          item = {
            id: `local-${Date.now()}`,
            type: this.activeAssetType || "material",
            name: file.name || `本地素材${Date.now()}`,
            cover: URL.createObjectURL(file)
          };
        }
        if (item && item.name) {
          // 上传成功后：追加到当前任务素材池；登录态同时追加到独立资产库
          const key = `${item.type}:${item.name}`;
          if (!(this.taskAssetLibrary || []).some(x => `${x.type}:${x.name}` === key)) {
            this.taskAssetLibrary = [...(this.taskAssetLibrary || []), item];
          }
          if (token && !(this.assetLibrary || []).some(x => String(x.id) === String(item.id))) {
            this.assetLibrary = [...this.assetLibrary, item];
          }
          if (token) {
            await this.persistEpisodeAssetBindings([item], true);
          }
        }
      } catch (e) {
        window.alert(e && e.message ? e.message : "本地上传失败");
      } finally {
        if (input) input.value = "";
      }
    },
    mentionTypeLabel(type) {
      const t = this.normalizeMentionEntityType(type);
      if (t === "role") return "角色";
      if (t === "scene") return "场景";
      return "素材";
    },
    /** 与下方资产条展示一致：非 role/scene/material 或未填写类型一律按素材参与 @ 候选（含历史 product 类型） */
    normalizeMentionEntityType(type) {
      const s = String(type || "")
        .trim()
        .toLowerCase();
      if (s === "role" || s === "character") return "role";
      if (s === "scene") return "scene";
      if (s === "material" || s === "product" || s === "prop" || s === "image" || s === "video") return "material";
      return "material";
    },
    onPromptInput() {
      const editor = this.$refs.promptEditor;
      const selection = window.getSelection();
      if (!editor) {
        this.mentionVisible = false;
        return;
      }
      this.description = editor.innerText || "";
      let textBefore = this.description;
      if (selection && selection.rangeCount > 0) {
        textBefore = this.getTextBeforeCaret(editor, selection);
      }
      const tb = String(textBefore || "").replace(/\uFF20/g, "@");
      // 允许连续输入 @@@、全角 ＠，以及 @ 后带空格再输入（如 "@ s"）也能触发候选
      const match = tb.match(/@\s*([^\s@]*)$/);
      if (!match) {
        this.mentionVisible = false;
        this.mentionQuery = "";
        return;
      }
      this.mentionQuery = String(match[1] || "").trim();
      this.mentionVisible = true;
      this.syncCurrentStoryboardPrompt(false);
      this.refreshChosenAssetsFromPrompt();
    },
    onPromptKeydown(event) {
      if (event.key === "@") {
        // 输入 @ 的瞬间先拉起候选，避免某些浏览器 contenteditable 时序导致不触发
        this.$nextTick(() => {
          this.mentionQuery = "";
          this.mentionVisible = true;
        });
      }
      if (!this.mentionVisible) return;
      if (event.key === "Enter") {
        event.preventDefault();
        if (this.mentionOptions.length) {
          this.insertMention(this.mentionOptions[0]);
        }
      }
      if (event.key === "Escape") {
        this.mentionVisible = false;
      }
    },
    onPromptBlur() {
      if (this._promptBlurTimer) {
        clearTimeout(this._promptBlurTimer);
        this._promptBlurTimer = null;
      }
      this._promptBlurTimer = setTimeout(() => {
        this._promptBlurTimer = null;
        this.mentionVisible = false;
        if (Date.now() < (this._suppressPromptBlurSyncUntil || 0)) {
          this.refreshChosenAssetsFromPrompt();
          return;
        }
        this.autoTagMentionsInEditor();
        const editor = this.$refs.promptEditor;
        if (editor) {
          this.description = editor.innerText || "";
        }
        this.syncCurrentStoryboardPrompt(true);
        this.refreshChosenAssetsFromPrompt();
      }, 120);
    },
    getTextBeforeCaret(editor, selection) {
      const range = selection.getRangeAt(0).cloneRange();
      range.selectNodeContents(editor);
      range.setEnd(selection.anchorNode, selection.anchorOffset);
      return range.toString();
    },
    insertMention(item) {
      const editor = this.$refs.promptEditor;
      const selection = window.getSelection();
      if (!editor || !selection || selection.rangeCount === 0) return;
      this.removeMentionTriggerBeforeCaret(selection);
      const range = selection.getRangeAt(0);
      const mention = this.createMentionNode(item);

      const trailingSpace = document.createTextNode(" ");
      range.deleteContents();
      range.insertNode(trailingSpace);
      range.insertNode(mention);
      range.setStartAfter(trailingSpace);
      range.setEndAfter(trailingSpace);
      selection.removeAllRanges();
      selection.addRange(range);
      this.mentionVisible = false;
      this.mentionQuery = "";
      editor.focus();
      this.description = editor.innerText || "";
      this.syncCurrentStoryboardPrompt(true);
      this.refreshChosenAssetsFromPrompt();
    },

    createMentionNode(item) {
      const mention = document.createElement("span");
      mention.className = "mention-tag";
      mention.setAttribute("contenteditable", "false");
      mention.setAttribute("data-id", String(item.id));
      mention.setAttribute("data-type", item.type);
      mention.setAttribute("data-label", item.name);

      const hasImg = item && item.cover && !this.isPlaceholderAsset(item.cover);
      if (hasImg) {
        const img = document.createElement("img");
        img.className = "mention-tag-img";
        img.src = item.cover;
        img.alt = item.name;
        img.setAttribute("draggable", "false");
        img.style.cursor = "zoom-in";
        img.addEventListener("click", evt => {
          evt.preventDefault();
          evt.stopPropagation();
          this.openMentionPreview(item);
        });
        mention.appendChild(img);
      }
      const text = document.createElement("span");
      text.className = "mention-tag-text";
      text.textContent = `@${item.name}`;
      mention.appendChild(text);
      return mention;
    },
    openImagePreviewLayer({ src, alt = "预览", errorItem = null, errorField = "cover" }) {
      const u = String(src || "").trim();
      if (!u) return;
      this.imagePreviewLayer = {
        visible: true,
        src: u,
        alt: String(alt || "预览"),
        errorItem,
        errorField: String(errorField || "cover")
      };
    },
    closeImagePreviewLayer() {
      this.imagePreviewLayer = {
        visible: false,
        src: "",
        alt: "预览",
        errorItem: null,
        errorField: "cover"
      };
    },
    onImagePreviewLayerImgError() {
      const { errorItem, errorField } = this.imagePreviewLayer;
      if (errorItem && errorField) this.onImgError(errorItem, errorField);
    },
    openMentionPreview(item) {
      if (!item) return;
      const u = String(item.cover || "").trim();
      if (!u) return;
      this.openImagePreviewLayer({
        src: u,
        alt: String(item.name || "预览"),
        errorItem: item,
        errorField: "cover"
      });
    },
    openStripPreview(url, errorItem) {
      if (this.isPlaceholderAsset(url)) return;
      const u = String(url || "").trim();
      if (!u) return;
      const alt =
        errorItem && String(errorItem.name || "").trim()
          ? String(errorItem.name)
          : "预览";
      this.openImagePreviewLayer({
        src: u,
        alt,
        errorItem: errorItem || null,
        errorField: "cover"
      });
    },
    resolveAssetLibraryRow(entityType, entityName) {
      const k = `${entityType}:${String(entityName || "").trim()}`;
      const lib = Array.isArray(this.assetLibrary) ? this.assetLibrary : [];
      return lib.find(a => `${a.type}:${String(a.name || "").trim()}` === k) || null;
    },
    stripMentionFromPrompt(name) {
      const n = String(name || "").trim();
      if (!n) return;
      const editor = this.$refs.promptEditor;
      let text = editor ? String(editor.innerText || "") : String(this.description || "");
      const escaped = n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const re = new RegExp("@" + escaped + "(?=[\\s@（）()，,。！？:：/／]|$)", "g");
      text = text.replace(re, "").replace(/[ \t]{2,}/g, " ");
      if (editor) {
        editor.innerText = text;
      }
      this.description = text;
      this.syncCurrentStoryboardPrompt(true);
    },
    async removeSupplementStripItem(asset) {
      const kind = asset && asset.stripKind;
      if (!kind || !asset) return;
      if (kind === "mention") {
        this.stripMentionFromPrompt(String(asset.name || ""));
        return;
      }
      const token = localStorage.getItem("user_token");
      const projectId = Number(this.projectId || 0);
      const episodeId = Number((this.episode && this.episode.id) || 0);
      if (!token || !projectId || !episodeId) {
        window.alert("请先登录并确保当前分集有效后再移除绑定");
        return;
      }
      try {
        if (kind === "task") {
          const key = `${asset.type}:${String(asset.name || "").trim()}`;
          this.taskAssetLibrary = (this.taskAssetLibrary || []).filter(
            x => `${x.type}:${String(x.name || "").trim()}` !== key
          );
          await this.persistMixedEpisodeBindings(this.taskAssetLibrary, false);
          return;
        }
        if (kind === "manual_episode") {
          const excludeKey = `${asset.type}:${String(asset.name || "").trim()}`;
          const picked = [];
          const seen = new Set();
          (this.taskAssetLibrary || []).forEach(it => {
            const k = `${it.type}:${String(it.name || "").trim()}`;
            if (k === excludeKey) return;
            picked.push(it);
            seen.add(k);
          });
          (this.currentEpisodeEntities || []).forEach(it => {
            if (String(it.bindSource || "") !== "manual_asset") return;
            const k = `${it.type}:${String(it.name || "").trim()}`;
            if (!it.name || k === excludeKey || seen.has(k)) return;
            const row = this.resolveAssetLibraryRow(it.type, it.name);
            if (row && Number(row.id) > 0) {
              picked.push({
                ...row,
                type: row.type || it.type || "material",
                name: String(row.name || it.name || "").trim()
              });
              seen.add(k);
            }
          });
          await this.persistMixedEpisodeBindings(picked, false);
        }
      } catch (e) {
        window.alert(e && e.message ? e.message : "移除失败");
      }
    },

    /** 半角 @ 与全角 ＠ */
    _isAtTriggerChar(ch) {
      return ch === "@" || ch === "\uFF20";
    },
    /** 已知实体名按长度降序，便于「@林逸站在」中优先命中「林逸的公寓」再「林逸」等最长前缀 */
    _sortedUniqueMentionNamesFromMap(byNameMap) {
      const keys = byNameMap && typeof byNameMap.keys === "function" ? Array.from(byNameMap.keys()) : [];
      const uniq = [...new Set(keys.map(k => String(k || "").trim()).filter(Boolean))];
      uniq.sort((a, b) => b.length - a.length);
      return uniq;
    },
    /**
     * 把编辑器内已存在的 mention-tag 的内部 img.src 刷成最新 cover。
     * 场景：步骤二重新生成图片后，分集绑定表与项目实体表的 preview_url 已变化，但前端 inline 标签 img
     * 仍是旧值；该方法只覆盖 src，不重建 DOM 结构（避免破坏光标 / 选区 / 事件）。
     */
    refreshMentionTagImagesInEditor() {
      const editor = this.$refs.promptEditor;
      if (!editor) return;
      const tags = editor.querySelectorAll(".mention-tag");
      if (!tags || !tags.length) return;
      const byName = this.mentionAssetByNameFirst;
      if (!byName || !byName.size) return;
      tags.forEach(tag => {
        const name = String(tag.getAttribute("data-label") || "").trim();
        if (!name) return;
        const asset = byName.get(name);
        if (!asset) return;
        const newUrl = this.mediaUrl(String((asset && asset.cover) || ""));
        if (!newUrl) return;
        const img = tag.querySelector("img.mention-tag-img");
        if (img && img.src !== newUrl) {
          img.src = newUrl;
        }
      });
    },
    autoTagMentionsInEditor() {
      // 1) 刷新已存在的 mention-tag 内部 img.src：步骤二重新生成图片后，
      //    分集绑定/项目实体的最新 preview_url 会通过 API 重新拉回，旧的 inline img 仍是旧图。
      this.refreshMentionTagImagesInEditor();
      const editor = this.$refs.promptEditor;
      if (!editor) return;
      // 预览关闭等时机会再次 autoTag；mention-tag-text 内有「@名称」若被当成正文会套娃嵌套 span
      if (editor.querySelector(".mention-tag .mention-tag")) {
        const plain = String(editor.innerText || "");
        editor.innerText = plain;
        this.description = plain;
      }
      const byName = this.mentionAssetByNameFirst;
      if (!byName || !byName.size) return;
      const sortedNames = this._sortedUniqueMentionNamesFromMap(byName);

      const isInsideMentionTag = node => {
        let p = node && node.parentNode;
        while (p && p !== editor) {
          if (p.nodeType === Node.ELEMENT_NODE && p.classList && p.classList.contains("mention-tag")) {
            return true;
          }
          p = p.parentNode;
        }
        return false;
      };

      const walker = document.createTreeWalker(editor, NodeFilter.SHOW_TEXT, {
        acceptNode: node => {
          if (!node || !node.nodeValue) return NodeFilter.FILTER_REJECT;
          if (isInsideMentionTag(node)) return NodeFilter.FILTER_REJECT;
          const nv = String(node.nodeValue || "");
          if (nv.indexOf("@") === -1 && nv.indexOf("\uFF20") === -1) return NodeFilter.FILTER_REJECT;
          return NodeFilter.FILTER_ACCEPT;
        }
      });

      const textNodes = [];
      while (walker.nextNode()) textNodes.push(walker.currentNode);

      textNodes.forEach(node => {
        const text = String(node.nodeValue || "");
        const frag = document.createDocumentFragment();
        let changed = false;
        let i = 0;
        while (i < text.length) {
          const c = text[i];
          if (!this._isAtTriggerChar(c)) {
            let nextAt = text.length;
            for (let j = i + 1; j < text.length; j++) {
              if (this._isAtTriggerChar(text[j])) {
                nextAt = j;
                break;
              }
            }
            frag.appendChild(document.createTextNode(text.slice(i, nextAt)));
            i = nextAt;
            continue;
          }
          const rest = text.slice(i + 1);
          let hitName = null;
          for (let si = 0; si < sortedNames.length; si++) {
            const name = sortedNames[si];
            if (rest.startsWith(name)) {
              hitName = name;
              break;
            }
          }
          if (hitName) {
            const asset = byName.get(hitName);
            if (asset) {
              frag.appendChild(this.createMentionNode(asset));
              changed = true;
              i += 1 + hitName.length;
              continue;
            }
          }
          frag.appendChild(document.createTextNode(c));
          i += 1;
        }
        if (!changed) return;
        node.parentNode && node.parentNode.replaceChild(frag, node);
      });
    },
    removeMentionTriggerBeforeCaret(selection) {
      const cursor = this.getCursorTextNode(selection);
      if (!cursor) return;
      const { node, offset } = cursor;
      const text = node.textContent || "";
      const prefix = text.slice(0, offset);
      const suffix = text.slice(offset);
      const match = prefix.match(/@[^@\s]*$/);
      if (!match) return;
      const start = prefix.length - match[0].length;
      node.textContent = `${prefix.slice(0, start)}${suffix}`;
      const range = document.createRange();
      range.setStart(node, start);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
    },
    getCursorTextNode(selection) {
      const anchorNode = selection.anchorNode;
      const anchorOffset = selection.anchorOffset;
      if (!anchorNode) return null;
      if (anchorNode.nodeType === Node.TEXT_NODE) {
        return { node: anchorNode, offset: anchorOffset };
      }
      if (anchorNode.nodeType !== Node.ELEMENT_NODE) return null;
      const prevSibling = anchorNode.childNodes[anchorOffset - 1];
      if (prevSibling && prevSibling.nodeType === Node.TEXT_NODE) {
        return {
          node: prevSibling,
          offset: (prevSibling.textContent || "").length
        };
      }
      const currentSibling = anchorNode.childNodes[anchorOffset];
      if (currentSibling && currentSibling.nodeType === Node.TEXT_NODE) {
        return { node: currentSibling, offset: 0 };
      }
      return null;
    },
    buildPromptPayload() {
      const editor = this.$refs.promptEditor;
      if (!editor) return { displayText: "", backendText: "", references: [] };
      let mentionIndex = 1;
      const references = [];
      const backendText = this.serializePromptNode(
        editor,
        () => mentionIndex++,
        mentionNode => {
          const id = Number(mentionNode.getAttribute("data-id") || 0);
          const type = String(mentionNode.getAttribute("data-type") || "").trim();
          const label = String(mentionNode.getAttribute("data-label") || "").trim();
          const source = this.mentionSourceLibrary || [];
          const matched = source.find(item => Number(item.id || 0) === id && String(item.type || "") === type)
            || source.find(item => String(item.name || "") === label);
          const url = String((matched && matched.cover) || "").trim();
          if (!url) return;
          const weightMap = { role: 0.9, scene: 0.7, material: 0.6 };
          const refRow = {
            type: "image",
            url,
            weight: weightMap[type] || 0.7,
            bindType: type,
            bindName: label
          };
          const libHit = (this.assetLibrary || []).find(
            item => Number(item.id || 0) === id && String(item.type || "") === type
          );
          if (libHit && Number(libHit.id) > 0) {
            refRow.libraryAssetId = Number(libHit.id);
            const vid = String(libHit.volcAssetId || "").trim();
            if (vid) refRow.volcAssetId = vid;
          }
          references.push(refRow);
        }
      );
      return {
        displayText: editor.innerText || "",
        backendText: backendText.replace(/\u00A0/g, " "),
        references: references.slice(0, 9)
      };
    },
    /** 同一 bindName（或同 url）只保留首条，与后端九宫格去重一致，避免 @Image 序号与参考列表错位须在调用方重算 backend。 */
    dedupeReferencesByBindName(refs) {
      const list = Array.isArray(refs) ? refs : [];
      const seen = new Set();
      const out = [];
      list.forEach(r => {
        if (!r || typeof r !== "object") return;
        const name = String(r.bindName || r.name || "")
          .trim()
          .replace(/[/／]+$/g, "");
        const url = String(r.url || r.imageUrl || "")
          .trim()
          .replace(/[/／]+$/g, "");
        const key = name || url;
        if (!key) return;
        if (seen.has(key)) return;
        seen.add(key);
        out.push(r);
      });
      return out.slice(0, 9);
    },
    /**
     * 将「@实体名」正文转为与 references 顺序一致的 @ImageN，供创建视频任务与多模态参考对齐（无绑定名的 @ 保持原样）。
     */
    promptDisplayToBackendAtImage(displayText, references) {
      const refs = Array.isArray(references) ? references.slice(0, 9) : [];
      const nameToIdx = new Map();
      refs.forEach((r, i) => {
        const n = String((r && r.bindName) || "")
          .trim()
          .replace(/[/／]+$/g, "");
        if (n && !nameToIdx.has(n)) nameToIdx.set(n, i + 1);
      });
      const names = [...nameToIdx.keys()].sort((a, b) => b.length - a.length);
      const s = String(displayText || "").replace(/\u00A0/g, " ");
      let out = "";
      let i = 0;
      while (i < s.length) {
        const c = s[i];
        if (c !== "@" && c !== "\uFF20") {
          out += c;
          i += 1;
          continue;
        }
        const rest = s.slice(i + 1);
        let hit = null;
        for (let si = 0; si < names.length; si++) {
          const n = names[si];
          if (rest.startsWith(n)) {
            hit = n;
            break;
          }
        }
        if (hit) {
          const k = nameToIdx.get(hit);
          out += `@Image${k}`;
          i += 1 + hit.length;
        } else {
          out += c;
          i += 1;
        }
      }
      return out;
    },
    serializePromptNode(root, nextMentionIndex, onMention) {
      let text = "";
      root.childNodes.forEach(node => {
        if (node.nodeType === Node.TEXT_NODE) {
          text += node.textContent || "";
          return;
        }
        if (node.nodeType === Node.ELEMENT_NODE && node.classList.contains("mention-tag")) {
          const index = nextMentionIndex();
          text += `@Image${index}`;
          if (typeof onMention === "function") onMention(node, index);
          return;
        }
        if (node.nodeType === Node.ELEMENT_NODE) {
          text += this.serializePromptNode(node, nextMentionIndex, onMention);
        }
      });
      return text;
    },
    selectTimelineItem(item) {
      if (this._promptBlurTimer) {
        clearTimeout(this._promptBlurTimer);
        this._promptBlurTimer = null;
      }
      // 先保存当前分镜的修改到 timelineItems（不立即写 sessionStorage，减少 IO）
      this.syncCurrentStoryboardPrompt(false);
      this.step4SyncingSelection = true;
      this._suppressPromptBlurSyncUntil = Date.now() + 400;

      const sid = Number((item && item.id) || 0);
      // 从 timelineItems 中获取最新数据（包含用户可能已修改但未保存的 prompt）
      const row = sid > 0 ? ((this.timelineItems || []).find(x => Number((x && x.id) || 0) === sid) || item) : item;

      // 更新当前选中的分镜数据
      this.storyboard = row;
      this.previewVideoUrl = this.mediaUrl((row && row.videoUrl) || "");

      // 更新时长/分辨率设置（来自分镜数据或全局设置）
      this.applyDurationFromStoryboard(row);
      this.applyResolutionFromStoryboard(row);

      // 设置描述文本（来自 timelineItems 中该分镜的 prompt）
      const raw = String((row && row.prompt) || "");
      this.description = this.normalizePromptForDisplay(raw);

      // 同步到编辑器 DOM
      this.$nextTick(() => {
        if (this.$refs.promptEditor) {
          this.$refs.promptEditor.innerText = this.description || "";
        }
        this.autoTagMentionsInEditor();
        this.refreshChosenAssetsFromPrompt();
        this.step4SyncingSelection = false;
      });
    },
    hasShotVideoUrl(item) {
      if (!item) return false;
      return Boolean(String(item.videoUrl || "").trim());
    },
    shotIsSkipped(item) {
      if (!item) return false;
      const sid = Number(item.id || 0);
      if (!sid) return false;
      return (this.skippedShotIds || []).map(Number).includes(sid);
    },
    toggleShotSkip(item) {
      if (!item) return;
      const sid = Number(item.id || 0);
      if (!sid) return;
      const list = (this.skippedShotIds || []).map(Number);
      const idx = list.indexOf(sid);
      if (idx >= 0) {
        list.splice(idx, 1);
      } else {
        list.push(sid);
      }
      this.skippedShotIds = list;
      this.persistSkippedShots();
    },
    persistSkippedShots() {
      try {
        const key = `ai_video_skipped_shots_${this.projectId || 'p'}`;
        sessionStorage.setItem(key, JSON.stringify(this.skippedShotIds || []));
      } catch (e) {}
    },
    hydrateSkippedShots() {
      try {
        const key = `ai_video_skipped_shots_${this.projectId || 'p'}`;
        const raw = sessionStorage.getItem(key);
        if (raw) this.skippedShotIds = JSON.parse(raw) || [];
      } catch (e) { this.skippedShotIds = []; }
    },
    async hydrateNextEpisode() {
      const pid = Number(this.projectId || 0);
      const curEpId = Number((this.episode && this.episode.id) || this.$route.query.id || 0);
      if (!pid || !curEpId) return;
      try {
        const detail = await userApi.projectDetail(pid);
        const eps = (detail && detail.episodes) || [];
        const sorted = eps.slice().sort((a, b) => Number(a && (a.no || a.episodeNo)) - Number(b && (b.no || b.episodeNo)));
        const idx = sorted.findIndex(ep => Number(ep && ep.id) === curEpId);
        this.nextEpisodeInfo = idx >= 0 && idx < sorted.length - 1 ? sorted[idx + 1] : null;
      } catch (e) {
        this.nextEpisodeInfo = null;
      }
    },
    async handleRegenerateSingleShot(item) {
      if (!item) return;
      this.selectTimelineItem(item);
      await this.$nextTick();
      await this.handleGenerateVideo();
    },
    /**
     * 「扩充描述」：调用后端单条充实接口（异步），把过短的 prompt 改为满足 7 段结构、≥100 字的密度文本。
     * 后端会自动写库；前端只需刷新 description 即可。
     */
    async handleExpandCurrentDescription() {
      if (this.expandingDescription) return;
      const shot = this.storyboard;
      if (!shot || !shot.id) {
        this.windowAlert && this.windowAlert('请先选择要扩充的分镜');
        return;
      }
      const shotId = Number(shot.id || 0);
      if (!shotId) {
        this.windowAlert && this.windowAlert('当前分镜缺少有效 ID，请刷新后重试');
        return;
      }
      const projectId = Number(this.projectId || 0);
      const episodeId = Number((this.episode && this.episode.id) || 0);
      const episodeNo = Number((this.episode && this.episode.no) || (this.episode && this.episode.episodeNo) || 0);
      const promptIn = String(this.description || '');
      this.expandingDescription = true;
      this.expandProgressMessage = '已提交，等待后台执行…';
      this.expandProgressPct = 5;
      let taskId = 0;
      let deduped = false;
      try {
        // 1) 提交异步任务（立刻返回 taskId，毫秒级响应，不再阻塞用户操作）
        const createResp = await userApi.createExpandShotPromptTask({
          projectId,
          episodeId,
          episodeNo,
          shotId,
          prompt: promptIn,
          durationSeconds: Number(this.parseDurationSeconds(this.selectedDuration)) || 12,
          sceneContext: (() => {
            const t = promptIn;
            const m = t.match(/场景[：:][^\n]+/);
            return m ? m[0] : '';
          })(),
        });
        if (!createResp || createResp.code !== 0) {
          this.windowAlert && this.windowAlert((createResp && createResp.msg) || '提交扩充任务失败，请稍后再试');
          return;
        }
        taskId = Number((createResp.data && createResp.data.taskId) || 0);
        if (!taskId) {
          // 兜底：服务端的同步接口退路（不期望走到这里）
          if (createResp.data && createResp.data.prompt) {
            this._applyExpandedPrompt(shot, String(createResp.data.prompt));
          } else {
            this.windowAlert && this.windowAlert('后端未返回任务 ID');
          }
          return;
        }
        if (createResp.data && createResp.data.dedup) {
          deduped = true;
        }
        if (this.$message && this.$message.info && !deduped) {
          this.$message.info('已提交扩充任务，后台生成中（可继续浏览其它分镜）');
        }
        // 2) 轮询任务状态，最多 90s（匹配 LLM 调用 + retry 预期时长）
        await this._pollExpandTask(taskId, shotId, shot, 90000, {
          // 期间允许用户切换到别的分镜：仅当仍然选中同一分镜时才更新 UI
          isCurrentShot: () => Number((this.storyboard && this.storyboard.id) || 0) === shotId,
        });
      } catch (err) {
        this.windowAlert && this.windowAlert('扩充异常：' + (err && err.message ? err.message : String(err)));
      } finally {
        this.expandingDescription = false;
        this.expandProgressPct = 0;
        this.expandProgressMessage = '';
      }
    },
    /**
     * 轮询异步「扩充描述」任务。支持中途切换分镜：
     *   - 如果用户切换到其它分镜，仅停止 UI 更新，但 task 仍继续；
     *   - 一旦 finished（success/failed），无论是否还选中同一分镜都清理轮询定时器。
     */
    _pollExpandTask(taskId, shotId, shot, maxMs, opts) {
      return new Promise((resolve) => {
        const basePollInterval = 1500;
        let pollInterval = basePollInterval;
        const startedAt = Date.now();
        const isCurrentShot = (opts && typeof opts.isCurrentShot === 'function') ? opts.isCurrentShot : () => true;
        let stopped = false;
        const tick = async () => {
          if (stopped) return;
          if (Date.now() - startedAt > maxMs) {
            stopped = true;
            if (this.$message && this.$message.warning) {
              this.$message.warning('扩充任务轮询超时，请稍后查看是否完成');
            }
            resolve('timeout');
            return;
          }
          // 后台 tab 时跳过本次请求，按基础间隔再次调度；切回前台后 visibilitychange 会触发补轮询
          if (typeof document !== 'undefined' && document.hidden) {
            this._expandProgressTimer = setPollingTimeout(tick, pollInterval);
            return;
          }
          try {
            const resp = await userApi.getExpandShotPromptTaskStatus({ taskId });
            if (!resp || resp.code !== 0) {
              // 暂时性错误：继续轮询，下次再试
              this._expandProgressTimer = setPollingTimeout(tick, pollInterval);
              return;
            }
            const data = resp.data || {};
            const status = String(data.status || 'pending');
            const pct = Number(data.progress || 0);
            const msg = String(data.message || '');
            if (isCurrentShot()) {
              this.expandProgressPct = pct;
              this.expandProgressMessage = msg;
            }
            if (status === 'success') {
              const result = data.result || {};
              const newPrompt = String(result.prompt || '');
              if (!newPrompt) {
                if (this.$message && this.$message.error) {
                  this.$message.error('扩充任务完成，但未返回新描述');
                }
                stopped = true;
                resolve('success_empty');
                return;
              }
              // 仅当仍选中同一分镜时写回 UI；但始终写库字段 shot.prompt
              shot.prompt = newPrompt;
              if (isCurrentShot()) {
                this._applyExpandedPrompt(shot, newPrompt);
              } else {
                // 已切到其它分镜：在队列/列表同步即可，不打扰用户当前焦点
                this.$nextTick(() => {
                  try { this.syncCurrentStoryboardPrompt(true); } catch (e) { /* ignore */ }
                });
              }
              if (isCurrentShot() && this.$message && this.$message.success) {
                this.$message.success(`已扩充分镜描述至 ${this.countPromptChars(newPrompt)} 字`);
              }
              stopped = true;
              resolve('success');
              return;
            }
            if (status === 'failed') {
              const errMsg = String(data.errorMessage || data.message || '扩充失败');
              if (this.$message && this.$message.error) {
                this.$message.error(errMsg);
              }
              stopped = true;
              resolve('failed');
              return;
            }
            // pending/running：指数退避（最多 5s），减少无效请求
            pollInterval = expBackoff(pollInterval, basePollInterval, 5000, 1.5);
            this._expandProgressTimer = setPollingTimeout(tick, pollInterval);
          } catch (e) {
            // 静默：下次再试
            this._expandProgressTimer = setPollingTimeout(tick, pollInterval);
          }
        };
        // 首次 200ms 快速探活，与原行为一致
        this._expandProgressTimer = setPollingTimeout(tick, 200, { jitter: false });
      });
    },
    /**
     * 把后端返回的扩充后的 prompt 写回当前分镜的 UI + 触发后台落库。
     */
    _applyExpandedPrompt(shot, newPrompt) {
      try {
        const display = this.normalizePromptForDisplay(newPrompt);
        this.description = display;
        if (this.$refs.promptEditor) {
          this.$refs.promptEditor.innerText = display;
        }
        shot.prompt = newPrompt;
        this.syncCurrentStoryboardPrompt(true);
      } catch (e) {
        /* ignore */
      }
    },
    /**
     * 统计 prompt 真实字符数（去除镜号头、画风头等元信息）
     */
    countPromptChars(text) {
      if (!text) return 0;
      let s = String(text).replace(/\r\n/g, '\n');
      s = s.replace(/^\s*\d{1,2}-\d{1,2}\s*【?\s*\d{1,2}\s*[秒sS]?\s*】?[\s\S]*?\n/u, '');
      s = s.replace(/画面风格[:：].*\n/u, '');
      s = s.replace(/【统一.*?】.*?\n/u, '');
      return Array.from(s.trim()).length;
    },
    async goToNextEpisode() {
      const pid = Number(this.projectId || 0);
      const curEpId = Number((this.episode && this.episode.id) || this.$route.query.id || 0);
      if (!pid || !curEpId) {
        window.alert("缺少项目或分集信息，无法跳转");
        return;
      }
      if (this.nextEpisodeLoading) return;
      this.nextEpisodeLoading = true;
      try {
        const detail = await userApi.projectDetail(pid);
        const episodes = (detail && detail.episodes) || [];
        const sorted = episodes
          .slice()
          .sort((a, b) => Number(a && (a.no || a.episodeNo)) - Number(b && (b.no || b.episodeNo)));
        const idx = sorted.findIndex(ep => Number(ep && ep.id) === curEpId);
        if (idx === -1 || idx >= sorted.length - 1) {
          window.alert("已是最后一集");
          return;
        }
        const next = sorted[idx + 1];
        this.$router.push({
          path: "/project/storyboard-editor",
          query: { id: String(next.id), projectId: String(pid) }
        }).catch(() => {});
      } catch (e) {
        window.alert(String((e && e.message) || e || "加载失败"));
      } finally {
        this.nextEpisodeLoading = false;
      }
    },
    extractAtNames(text) {
      const s = String(text || "");
      const source = this.mentionSourceLibrary || [];
      const uniq = new Set();
      (source || []).forEach(x => {
        const n = String((x && x.name) || "")
          .trim()
          .replace(/[/／]+$/g, "");
        if (n) uniq.add(n);
      });
      const sortedNames = [...uniq].sort((a, b) => b.length - a.length);
      if (!sortedNames.length) {
        const out = [];
        const seen = new Set();
        const re = /@([^\s@（）()，,。！？:：/／]+)/g;
        let m;
        while ((m = re.exec(s))) {
          const name = String(m[1] || "")
            .replace(/[/／]+$/g, "")
            .trim();
          if (!name || seen.has(name)) continue;
          seen.add(name);
          out.push(name);
        }
        return out;
      }
      const out = [];
      const seen = new Set();
      let i = 0;
      while (i < s.length) {
        const c = s[i];
        if (c !== "@" && c !== "\uFF20") {
          i += 1;
          continue;
        }
        const rest = s.slice(i + 1);
        let hit = null;
        for (let si = 0; si < sortedNames.length; si++) {
          const name = sortedNames[si];
          if (rest.startsWith(name)) {
            hit = name;
            break;
          }
        }
        if (hit) {
          if (!seen.has(hit)) {
            seen.add(hit);
            out.push(hit);
          }
          i += 1 + hit.length;
        } else {
          i += 1;
        }
      }
      return out;
    },
    buildReferencesFromPromptText(text, options = {}) {
      const includeStripAssets = options.includeStripAssets !== undefined
        ? !!options.includeStripAssets
        : !!this.includeStripAssetsAsReferences;
      const names = this.extractAtNames(text);
      const source = this.mentionSourceLibrary || [];
      const byName = new Map(source.map(item => [String(item.name || ""), item]));
      const out = [];
      const seen = new Set();
      const weightMap = { role: 0.9, scene: 0.7, material: 0.6 };
      names.forEach(name => {
        const hit = byName.get(name);
        if (!hit) return;
        const cover = String(hit.cover || "").trim();
        if (!cover) return;
        const key = `${String(hit.type || "")}:${name}`;
        if (seen.has(key)) return;
        seen.add(key);
        const refRow = {
          type: "image",
          url: cover,
          weight: weightMap[String(hit.type || "")] || 0.7,
          bindType: String(hit.type || "image"),
          bindName: name
        };
        const libHit = (this.assetLibrary || []).find(
          item => String(item.name || "").trim() === name && String(item.type || "") === String(hit.type || "")
        );
        if (libHit && Number(libHit.id) > 0) {
          refRow.libraryAssetId = Number(libHit.id);
          const vid = String(libHit.volcAssetId || "").trim();
          if (vid) refRow.volcAssetId = vid;
        }
        out.push(refRow);
      });
      // 可选补充：把“已绑定但未显式 @”的参考对象加入引用，提升召回覆盖率
      if (includeStripAssets) {
        (this.assetStripAssets || []).forEach(item => {
          if (!item) return;
          const type = String(item.type || "").trim();
          const name = String(item.name || "").trim();
          const url = String(item.cover || "").trim();
          if (!name || !url) return;
          const key = `${type}:${name}`;
          if (seen.has(key)) return;
          seen.add(key);
          out.push({
            type: "image",
            url,
            weight: weightMap[type] || 0.7,
            bindType: type || "image",
            bindName: name
          });
        });
      }
      return out.slice(0, 9);
    },
    refreshChosenAssetsFromPrompt() {
      const editor = this.$refs.promptEditor;
      const rawText = editor ? (editor.innerText || "") : (this.description || "");
      this.description = rawText;
    },
    async persistEpisodeAssetBindings(pickedAssets, append = true) {
      await this.persistMixedEpisodeBindings(pickedAssets, append);
    },
    /**
     * 弹窗确认：区分 fa_ai_asset 与 fa_ai_project_entity（本项目实体），分别落库到分集关联表
     */
    async persistMixedEpisodeBindings(pickedAssets, append = true) {
      const token = localStorage.getItem("user_token");
      const projectId = Number(this.projectId || 0);
      const episodeId = Number((this.episode && this.episode.id) || 0);
      if (!token || !projectId || !episodeId) return;
      const assetIds = [];
      const entityIds = [];
      (pickedAssets || []).forEach(a => {
        const sid = String(a && a.id !== undefined && a.id !== null ? a.id : "");
        const dm = sid.match(/^d-(role|scene|material|product)-(\d+)$/);
        // product 前缀为历史数据，实体 id 与 material 相同
        if (dm) {
          entityIds.push(Number(dm[2]));
          return;
        }
        const ep = sid.match(/^ep-(\d+)$/);
        if (ep) {
          entityIds.push(Number(ep[1]));
          return;
        }
        const n = Number(a && a.id);
        if (Number.isFinite(n) && n > 0) assetIds.push(n);
      });
      if (append && !assetIds.length && !entityIds.length) return;
      try {
        if (!append) {
          await userApi.bindEpisodeProjectEntities({
            projectId,
            episodeId,
            entityIds: JSON.stringify(entityIds),
            append: 0
          });
          await userApi.bindEpisodeAssets({
            projectId,
            episodeId,
            assetIds: JSON.stringify(assetIds),
            append: 0
          });
        } else {
          if (entityIds.length) {
            await userApi.bindEpisodeProjectEntities({
              projectId,
              episodeId,
              entityIds: JSON.stringify(entityIds),
              append: 1
            });
          }
          if (assetIds.length) {
            await userApi.bindEpisodeAssets({
              projectId,
              episodeId,
              assetIds: JSON.stringify(assetIds),
              append: 1
            });
          }
        }
        await this.loadEpisodeEntitiesFromApi();
      } catch (e) {
        window.alert(e && e.message ? e.message : "分集素材绑定失败");
      }
    },
    shotHasVideo(shot) {
      if (!shot) return false;
      const id = Number(shot.id || 0);
      if (String(shot.videoUrl || "").trim()) return true;
      const records = this.historyRecordsByStoryboardId[id] || [];
      return records.some(r => String((r && r.videoUrl) || "").trim());
    },
    isShotVideoGenerating(shotId) {
      const gen = this.videoGenByShotId[Number(shotId)];
      if (!gen) return false;
      const st = String(gen.status || "");
      return st === "pending" || st === "running";
    },
    isShotVideoGenFailed(shotId) {
      const gen = this.videoGenByShotId[Number(shotId)];
      return gen && String(gen.status || "") === "failed";
    },
    isShotVideoNotGenerated(item) {
      if (!item) return false;
      const shotId = Number(item.id || 0);
      if (!shotId) return false;
      if (this.isShotVideoGenerating(shotId) || this.isShotVideoGenFailed(shotId)) return false;
      return !this.shotHasVideo(item);
    },
    isShotVideoGenDone(item) {
      if (!item) return false;
      const shotId = Number(item.id || 0);
      if (!shotId) return false;
      if (this.isShotVideoGenerating(shotId) || this.isShotVideoGenFailed(shotId)) return false;
      return this.shotHasVideo(item);
    },
    shotVideoGenBadgeText(item) {
      const shotId = Number(item && item.id);
      if (!shotId) return "";
      const gen = this.videoGenByShotId[shotId];
      if (gen) {
        const st = String(gen.status || "");
        const p = Number(gen.progress || 0);
        if (st === "pending") {
          return p > 0 ? "生成中" : "排队中";
        }
        if (st === "running") {
          return "生成中";
        }
        if (st === "failed") {
          const msg = String((gen && gen.message) || "");
          return mapStep4VideoErrorHint(msg);
        }
      }
      if (this.shotHasVideo(item)) return "已生成";
      return "未生成";
    },
    shotVideoGenBadgeClass(item) {
      const shotId = Number(item && item.id);
      if (!shotId) return {};
      if (this.isShotVideoGenFailed(shotId)) return { "is-failed": true };
      if (this.isShotVideoGenerating(shotId)) return { "is-generating": true };
      if (this.shotHasVideo(item)) return { "is-done": true };
      return { "is-idle": true };
    },
    registerVideoGenTask(shotId, info) {
      const sid = Number(shotId);
      if (!sid) return;
      this.$set(this.videoGenByShotId, sid, {
        taskId: Number((info && info.taskId) || 0),
        status: String((info && info.status) || "pending"),
        progress: Number((info && info.progress) || 0),
        message: String((info && (info.message || info.errorMessage)) || "")
      });
      if (Number(this.storyboard && this.storyboard.id) === sid) {
        this.generatingVideoStoryboardId = sid;
      }
    },
    updateVideoGenFromPollStatus(shotId, st) {
      const sid = Number(shotId);
      if (!sid || !this.videoGenByShotId[sid]) return;
      const status = String((st && st.status) || "");
      let mapped = "running";
      if (status === "pending") {
        const p = Number((st && st.progress) || 0);
        mapped = p > 0 ? "running" : "pending";
      } else if (status === "running") mapped = "running";
      else if (status === "failed") mapped = "failed";
      const rawMsg = String((st && (st.message || st.errorMessage)) || "");
      const msg = mapped === "failed" ? mapStep4VideoErrorHint(rawMsg) : rawMsg;
      this.$set(this.videoGenByShotId, sid, {
        ...this.videoGenByShotId[sid],
        status: mapped,
        progress: Number((st && st.progress) || 0),
        message: msg
      });
    },
    markVideoGenFailed(shotId, err) {
      const sid = Number(shotId);
      if (!sid) return;
      const raw = String((err && err.message) || err || "");
      const hint = mapStep4VideoErrorHint(raw);
      this.$set(this.videoGenByShotId, sid, {
        ...(this.videoGenByShotId[sid] || {}),
        status: "failed",
        progress: 0,
        message: hint
      });
      if (this.generatingVideoStoryboardId === sid) this.generatingVideoStoryboardId = null;
    },
    clearVideoGenTask(shotId) {
      const sid = Number(shotId);
      if (!sid) return;
      if (this.videoGenByShotId[sid]) this.$delete(this.videoGenByShotId, sid);
      if (this.generatingVideoStoryboardId === sid) this.generatingVideoStoryboardId = null;
    },
    findTimelineShotById(shotId) {
      const sid = Number(shotId || 0);
      if (!sid) return null;
      return (this.timelineItems || []).find(it => Number(it && it.id) === sid) || null;
    },
    async resumeStep4VideoTasks() {
      const projectId = Number(this.projectId || 0);
      if (!projectId || !this.step4TimelineHydrated) return;
      try {
        const res = await userApi.listStep4VideoTasks({ projectId });
        const tasks = (res && res.tasks) || [];
        tasks.forEach(t => {
          const shotId = Number((t && t.shotId) || 0);
          const taskId = Number((t && t.taskId) || 0);
          if (!shotId) return;
          const status = String((t && t.status) || "");
          const shotRow = this.findTimelineShotById(shotId);
          if (status === "failed") {
            if (shotRow && this.shotHasVideo(shotRow)) return;
            const errMsg = String((t && (t.errorMessage || t.message)) || "");
            this.markVideoGenFailed(shotId, { message: errMsg });
            return;
          }
          if ((status === "pending" || status === "running") && taskId > 0) {
            this.registerVideoGenTask(shotId, t);
            this.startVideoGenPollInBackground(shotId, taskId, 20000);
          }
        });
      } catch (_) {
        /* 无进行中任务或接口不可用时不阻断页面 */
      }
    },
    startVideoGenPollInBackground(shotId, taskId, intervalMs = 20000) {
      const sid = Number(shotId);
      const tid = Number(taskId);
      if (!sid || !tid) return;
      if (!this._activeVideoGenPolls) this._activeVideoGenPolls = {};
      if (this._activeVideoGenPolls[sid]) return;
      this._activeVideoGenPolls[sid] = true;
      // 注册后启动共享批量轮询器：所有 shot 共用 1 个 setTimeout + 1 个 batch HTTP 请求
      this.ensureStep4BatchPoll();
      (async () => {
        try {
          const result = await this.pollVideoTaskUntilDone(tid, intervalMs, sid);
          this.applyVideoGenSuccess(sid, result, {});
        } catch (e) {
          const raw = String((e && e.message) || e || "");
          if (/视频任务超时/.test(raw)) {
            this.registerVideoGenTask(sid, {
              taskId: tid,
              status: "running",
              progress: Number((this.videoGenByShotId[sid] && this.videoGenByShotId[sid].progress) || 0),
              message: "生成耗时较长，后台继续处理，请稍候或刷新页面"
            });
            delete this._activeVideoGenPolls[sid];
            this.startVideoGenPollInBackground(sid, tid, 30000);
            return;
          }
          this.markVideoGenFailed(sid, e);
          if (Number(this.storyboard && this.storyboard.id) === sid) {
            alertStep4VideoError(raw);
          }
        } finally {
          delete this._activeVideoGenPolls[sid];
        }
      })();
    },
    /** 启动 / 维持一个共享批量轮询定时器，所有待完成的视频任务一起查 */
    ensureStep4BatchPoll() {
      if (this._batchVideoPollTimer) return;
      const schedule = () => {
        if (this._batchVideoPollTimer) {
          clearTimeout(this._batchVideoPollTimer);
          this._batchVideoPollTimer = null;
        }
        if (typeof document !== 'undefined' && document.hidden) {
          // 后台 tab 不轮询；visibilitychange 时重入
          return;
        }
        const activeCount = Object.keys(this.videoGenByShotId || {}).filter(k => {
          const v = this.videoGenByShotId[k];
          if (!v) return false;
          const st = String(v.status || "");
          return st === "pending" || st === "running";
        }).length;
        if (activeCount === 0) {
          return;
        }
        // 间隔随活跃任务数自适应 + 抖动
        const base = Math.max(16000, this._step4BatchPollMs || 20000);
        const scaled = Math.min(60000, base + Math.floor((activeCount - 1) * 1600));
        const jitter = Math.floor(scaled * 0.2 * (Math.random() * 2 - 1));
        const wait = Math.max(16000, scaled + jitter);
        this._batchVideoPollTimer = setTimeout(async () => {
          this._batchVideoPollTimer = null;
          if (this._batchVideoPollInFlight) {
            schedule();
            return;
          }
          this._batchVideoPollInFlight = true;
          try {
            await this.tickStep4BatchPoll();
          } finally {
            this._batchVideoPollInFlight = false;
          }
          schedule();
        }, wait);
      };
      schedule();
    },
    /** 一次 batchGetGenerateStoryboardVideoTaskStatus 拉所有活跃 taskId 并更新 videoGenByShotId */
    async tickStep4BatchPoll() {
      const active = Object.keys(this.videoGenByShotId || {})
        .map(k => ({ shotId: Number(k), taskId: Number((this.videoGenByShotId[k] || {}).taskId || 0) }))
        .filter(x => x.taskId > 0);
      if (!active.length) return;
      const taskIds = active.map(x => x.taskId);
      let resp;
      try {
        resp = await userApi.batchGetGenerateStoryboardVideoTaskStatus({ taskIds });
      } catch (e) {
        return;
      }
      const list = (resp && Array.isArray(resp.tasks)) ? resp.tasks : [];
      const byId = {};
      list.forEach(t => { if (t && t.taskId) byId[Number(t.taskId)] = t; });
      active.forEach(({ shotId, taskId }) => {
        const st = byId[taskId];
        if (!st) return;
        const status = String(st.status || "");
        if (status === "success") {
          this.applyVideoGenSuccess(shotId, (st.result || {}), {});
        } else if (status === "failed") {
          this.markVideoGenFailed(shotId, new Error(String(st.errorMessage || st.message || "视频生成失败")));
          if (Number(this.storyboard && this.storyboard.id) === shotId) {
            alertStep4VideoError(String(st.errorMessage || st.message || ""));
          }
        } else {
          this.updateVideoGenFromPollStatus(shotId, st);
        }
      });
      if (resp && resp.pollIntervalMs) {
        this._step4BatchPollMs = Math.max(16000, Number(resp.pollIntervalMs) || 20000);
      }
    },
    async runVideoGenPoll(shotId, taskId, intervalMs = 20000) {
      const sid = Number(shotId);
      const tid = Number(taskId);
      if (!sid || !tid) throw new Error("无效的视频任务");
      if (!this._activeVideoGenPolls) this._activeVideoGenPolls = {};
      if (this._activeVideoGenPolls[sid]) {
        return this.pollVideoTaskUntilDone(tid, intervalMs, sid);
      }
      this._activeVideoGenPolls[sid] = true;
      try {
        return await this.pollVideoTaskUntilDone(tid, intervalMs, sid);
      } finally {
        delete this._activeVideoGenPolls[sid];
      }
    },
    applyVideoGenSuccess(shotId, taskData, opts = {}) {
      this.clearVideoGenTask(Number(shotId));
      const storyboardId = Number(shotId);
      if (!storyboardId) return;
      const safeSeconds = this.clampDurationSeconds(
        Number(opts.durationSeconds || 0) ||
          this.parseDurationSeconds(opts.durationText || this.selectedDuration)
      );
      const safeDuration = `${safeSeconds}s`;
      const savedPrompt = String(opts.savedPrompt || "");
      const backendPrompt = String(opts.backendPrompt || "");
      const newRecord = {
        id: `r${Date.now()}_${storyboardId}`,
        model: String(opts.model || this.selectedModel),
        resolution: String(opts.resolution || this.selectedResolution),
        time: new Date().toLocaleString("zh-CN"),
        duration: safeDuration,
        status: "已消耗",
        cover: String(opts.cover || (this.storyboard && this.storyboard.cover) || ""),
        videoUrl: taskData && taskData.videoUrl ? taskData.videoUrl : "",
        promptDisplay: savedPrompt ? this.normalizePromptForDisplay(savedPrompt) : "",
        promptForBackend: backendPrompt,
        pointsCharged:
          taskData && typeof taskData.pointsCharged === "number" ? taskData.pointsCharged : this.generatePoints,
        pointsPerSecond: this.videoPointsPerSecond
      };
      const existing = this.historyRecordsByStoryboardId[storyboardId] || [];
      this.$set(this.historyRecordsByStoryboardId, storyboardId, [newRecord, ...existing]);
      const vUrl = newRecord.videoUrl ? this.mediaUrl(newRecord.videoUrl) : "";
      if (vUrl) {
        const idx2 = (this.timelineItems || []).findIndex(it => Number(it && it.id) === storyboardId);
        if (idx2 !== -1) {
          const coverFromResult =
            taskData && taskData.videoCoverUrl ? this.mediaUrl(taskData.videoCoverUrl) : "";
          this.$set(this.timelineItems, idx2, {
            ...this.timelineItems[idx2],
            videoUrl: vUrl,
            ...(coverFromResult ? { videoCoverUrl: coverFromResult } : {})
          });
          if (Number(this.storyboard && this.storyboard.id) === storyboardId) {
            this.storyboard = this.timelineItems[idx2];
            this.previewVideoUrl = vUrl;
          }
        }
      }
      this.notifyPointsChanged();
      this.maybeAutoAdvanceAfterShot(storyboardId);
      return newRecord;
    },
    maybeAutoAdvanceAfterShot(shotId) {
      const list = Array.isArray(this.timelineItems) ? this.timelineItems : [];
      if (!list.length) return;
      const idx = list.findIndex(it => Number(it && it.id) === Number(shotId));
      if (idx === -1) return;
      const stillUndoneHere = list.some((it, i) =>
        i > idx && !this.shotIsSkipped(it) && !String((it && it.videoUrl) || "").trim()
      );
      if (stillUndoneHere) return;
      const prevUnfinished = list.slice(0, idx).some(it =>
        !this.shotIsSkipped(it) && !String((it && it.videoUrl) || "").trim()
      );
      if (prevUnfinished) return;
      // 当前集已无可生成镜头（剩「跳过」或全完成），尝试跨集跳到下一集首个未完成分镜
      const pid = Number(this.projectId || 0);
      const currentEpId = Number((this.episode && this.episode.id) || this.$route.query.id || 0);
      if (!pid || !currentEpId) return;
      this.tryAutoAdvanceToNextEpisode(pid, currentEpId);
    },
    async tryAutoAdvanceToNextEpisode(projectId, currentEpisodeId) {
      if (this._advancingToNextEpisode) return;
      try {
        const detail = await userApi.projectDetail(projectId);
        const episodes = Array.isArray(detail && detail.episodes) ? detail.episodes : [];
        const sorted = episodes
          .slice()
          .sort((a, b) => Number(a && (a.no || a.episodeNo)) - Number(b && (b.no || b.episodeNo)));
        const idx = sorted.findIndex(ep => Number(ep && ep.id) === Number(currentEpisodeId));
        if (idx === -1 || idx >= sorted.length - 1) return;
        for (let i = idx + 1; i < sorted.length; i++) {
          const ep = sorted[i];
          const shots = Array.isArray(ep && ep.shots) ? ep.shots : [];
          const target = shots.find(s => !String((s && (s.videoUrl || s.video_url)) || "").trim());
          if (!target) continue;
          this._advancingToNextEpisode = true;
          this.$message && this.$message.success && this.$message.success(`本集已生成完毕，已为您跳到第 ${Number(ep.no || ep.episodeNo) || (i + 1)} 集「${ep.title || ''}」下一未完成分镜`);
          this.$router.push({
            path: "/project/storyboard-editor",
            query: {
              id: String(ep.id),
              projectId: String(projectId)
            }
          }).catch(() => {});
          return;
        }
      } catch (e) {
        /* 静默失败：不影响当前已完成 */
      } finally {
        setTimeout(() => { this._advancingToNextEpisode = false; }, 800);
      }
    },
    async handleGenerateVideo() {
      const sid = Number((this.storyboard && this.storyboard.id) || 0);
      if (this.batchGenerating || this.isShotVideoGenerating(sid)) {
        return;
      }
      if (!this.step4TimelineHydrated) {
        window.alert("分镜数据加载中，请稍候再生成视频");
        return;
      }
      this.syncCurrentStoryboardPrompt(true);
      if (this.videoPointsPerSecond <= 0) {
        window.alert("当前模型/清晰度未配置扣费价格，请先在后台配置 fa_ai_points_config（step4_generate_video）。");
        return;
      }
      if (!this.storyboard) return;
      const storyboardId = this.storyboard.id;
      const step4Ctx = this.buildStep4PersistContext(this.storyboard);
      const targetShotId = this.resolveShotId(this.storyboard);
      if (
        !(
          targetShotId > 0 ||
          (step4Ctx.episodeId > 0 && step4Ctx.shotNo > 0) ||
          (step4Ctx.episodeNo > 0 && step4Ctx.shotNo > 0)
        )
      ) {
        window.alert("当前分镜缺少有效 shotId，请先刷新分镜数据后重试");
        return;
      }
      const hasHistory = this.shotHasVideo(this.storyboard);
      const effectiveShotId = Number(targetShotId || storyboardId);
      const promptPayload = this.buildPromptPayload();
      const referencesDeduped = this.dedupeReferencesByBindName(promptPayload.references || []);
      const backendPrompt = this.normalizePromptForBackend(
        this.promptDisplayToBackendAtImage(String(promptPayload.displayText || ""), referencesDeduped)
      );
      const persistPrompt = this.normalizePromptForBackend(
        this.expandAtImagePlaceholdersInPrompt(String(promptPayload.displayText || ""))
      );
      if (!backendPrompt || !persistPrompt) {
        window.alert("请先填写分镜描述");
        return;
      }
      // 强制：生成时长不超过 15s（与下拉一致）
      const safeSeconds = this.clampDurationSeconds(this.parseDurationSeconds(this.selectedDuration));
      const safeDuration = `${safeSeconds}s`;

      // 立刻反馈「生成中」，勿等 saveStoryboardPrompt / createTask 完成（与 generateButtonText 同源 shotId）
      let uiShotId = Number((this.storyboard && this.storyboard.id) || effectiveShotId);
      this.registerVideoGenTask(uiShotId, { taskId: 0, status: "pending" });

      try {
        // 先落库最新 prompt，避免“编辑器最新内容未保存”导致生成与页面不一致
        const saveRes = await userApi.saveStoryboardPrompt({
          projectId: Number(this.projectId || 0),
          episodeId: step4Ctx.episodeId,
          episodeNo: step4Ctx.episodeNo,
          shotNo: step4Ctx.shotNo,
          storyboardId: Number(targetShotId || 0),
          shotId: Number(targetShotId || 0),
          prompt: persistPrompt
        });
        const savedPrompt = this.normalizePromptForBackend(
          this.expandAtImagePlaceholdersInPrompt(String((saveRes && saveRes.prompt) || persistPrompt))
        );
        this.storyboard.prompt = savedPrompt;
        const resolvedPk = Number((saveRes && saveRes.shotId) || 0);
        const idx = (this.timelineItems || []).findIndex(it => {
          const pk = Number(it && it.id);
          const no = Number(it && it.shotNo);
          return (
            (targetShotId > 0 && pk === Number(targetShotId)) ||
            (step4Ctx.shotNo > 0 && no === step4Ctx.shotNo) ||
            (resolvedPk > 0 && pk === resolvedPk)
          );
        });
        if (idx !== -1) {
          this.$set(this.timelineItems, idx, {
            ...this.timelineItems[idx],
            ...(resolvedPk > 0 ? { id: resolvedPk } : {}),
            prompt: savedPrompt
          });
          const selNo = Number(this.storyboard && this.storyboard.shotNo);
          if (selNo === Number(this.timelineItems[idx].shotNo)) {
            this.storyboard = this.timelineItems[idx];
          }
        }
        const persistedShotId = Number(resolvedPk || targetShotId || storyboardId || uiShotId);
        if (persistedShotId > 0 && persistedShotId !== uiShotId) {
          this.clearVideoGenTask(uiShotId);
          uiShotId = persistedShotId;
          this.registerVideoGenTask(uiShotId, { taskId: 0, status: "pending" });
        }
        this.updateShotPromptInSessionCache(persistedShotId, savedPrompt);

        await userApi.saveStoryboardVideoConfig({
          projectId: Number(this.projectId || 0),
          episodeId: step4Ctx.episodeId,
          episodeNo: step4Ctx.episodeNo,
          shotNo: step4Ctx.shotNo,
          storyboardId: Number(targetShotId || persistedShotId || 0),
          shotId: Number(persistedShotId || targetShotId || 0),
          duration: safeDuration,
          durationSeconds: Number(safeSeconds || 0),
          resolution: String(this.selectedResolution || "")
        });

        const createRes = await userApi.createGenerateStoryboardVideoTask({
          projectId: Number(this.projectId || 0),
          episodeId: step4Ctx.episodeId,
          episodeNo: step4Ctx.episodeNo,
          shotNo: step4Ctx.shotNo,
          storyboardId: Number(targetShotId || 0),
          shotId: Number(targetShotId || 0),
          modelCode: String(this.selectedModel || ""),
          resolution: String(this.selectedResolution || ""),
          durationSeconds: Number(safeSeconds || 0),
          prompt: backendPrompt,
          references: JSON.stringify(referencesDeduped)
        });
        const taskId = Number(createRes.taskId || 0);
        if (!taskId) {
          throw new Error("视频任务创建失败");
        }
        this.registerVideoGenTask(uiShotId, { taskId, status: "pending" });
        const taskData = await this.runVideoGenPoll(
          uiShotId,
          taskId,
          Number(createRes.pollIntervalMs || 10000)
        );
        const newRecord = this.applyVideoGenSuccess(uiShotId, taskData, {
          savedPrompt,
          backendPrompt,
          durationSeconds: safeSeconds,
          durationText: safeDuration,
          cover: this.storyboard.cover,
          model: this.selectedModel,
          resolution: this.selectedResolution
        });
        window.alert(`${hasHistory ? "重新生成" : "生成"}成功，消耗积分 ${newRecord.pointsCharged}`);
      } catch (e) {
        if (uiShotId > 0) this.markVideoGenFailed(uiShotId, e);
        alertStep4VideoError(String((e && e.message) || e || ""));
      }
    },
    async pollVideoTaskUntilDone(taskId, intervalMs = 20000, shotId = 0) {
      const start = Date.now();
      const softTimeoutMs = 20 * 60 * 1000;
      const hardTimeoutMs = 90 * 60 * 1000;
      let slowMode = false;
      let sleepMs = Math.max(20000, Math.min(120000, Number(intervalMs) || 20000));
      for (;;) {
        const st = await userApi.getGenerateStoryboardVideoTaskStatus({ taskId });
        const serverPollMs = Number((st && st.pollIntervalMs) || 0);
        if (serverPollMs > 0) {
          sleepMs = Math.max(sleepMs, Math.min(120000, serverPollMs));
        }
        if (Number(shotId) > 0) this.updateVideoGenFromPollStatus(shotId, st);
        const status = String((st && st.status) || "");
        if (status === "success") {
          const result = (st && st.result) || {};
          return result;
        }
        if (status === "failed") {
          const errMsg = String((st && (st.errorMessage || st.message)) || "");
          throw new Error(mapStep4VideoErrorHint(errMsg));
        }
        const elapsed = Date.now() - start;
        if (elapsed > hardTimeoutMs) {
          throw new Error("视频任务超时，请稍后刷新页面查看");
        }
        if (elapsed > softTimeoutMs && !slowMode) {
          slowMode = true;
          sleepMs = Math.max(sleepMs, 60000);
          if (Number(shotId) > 0 && this.videoGenByShotId[shotId]) {
            this.$set(this.videoGenByShotId, shotId, {
              ...this.videoGenByShotId[shotId],
              status: "running",
              message: "生成耗时较长，后台继续处理…"
            });
          }
        }
        await new Promise(resolve => setTimeout(resolve, sleepMs));
      }
    },
    historyRecordVideoUrl(record) {
      return String((record && record.videoUrl) || "").trim();
    },
    openVideoUrlInNewTab(rawUrl) {
      const url = this.mediaUrl(String(rawUrl || "").trim());
      if (!url) return false;
      try {
        const a = document.createElement("a");
        a.href = url;
        a.target = "_blank";
        a.rel = "noopener noreferrer";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        return true;
      } catch (_) {
        return false;
      }
    },
    downloadHistoryVideo(record) {
      const raw = this.historyRecordVideoUrl(record);
      if (!raw) return;
      if (!this.openVideoUrlInNewTab(raw)) {
        window.alert("视频地址无效，无法打开下载链接。");
      }
    },
    collectEpisodeVideoExports(shots) {
      const safeTitle = String(this.headerTitle || (this.episode && this.episode.title) || "分集")
        .replace(/[\\/:*?"<>|]/g, "_")
        .replace(/\s+/g, " ")
        .trim();
      const list = Array.isArray(shots) ? shots : [];
      const out = [];
      list.forEach((shot, idx) => {
        if (!shot || typeof shot !== "object") return;
        const raw = String(shot.videoUrl || shot.video_url || "").trim();
        if (!raw) return;
        const shotNo = Number(shot.shotNo || shot.no || shot.shot_no || idx + 1);
        const shotLabel = String(shot.title || "").trim();
        const namePart = shotLabel
          ? `${safeTitle || "分集"}-镜头${String(shotNo).padStart(2, "0")}-${shotLabel}`
          : `${safeTitle || "分集"}-镜头${String(shotNo).padStart(2, "0")}`;
        out.push({
          rawUrl: raw,
          filename: `${namePart.replace(/[\\/:*?"<>|]/g, "_")}.mp4`
        });
      });
      return out;
    },
    async fetchTimelineShotsForExport() {
      let shots = Array.isArray(this.timelineItems) ? this.timelineItems.map(x => ({ ...x })) : [];
      const pid = Number(this.$route.query.projectId || 0);
      const epId = Number((this.episode && this.episode.id) || this.$route.query.id || 0);
      if (pid > 0 && epId > 0 && localStorage.getItem("user_token")) {
        try {
          const row = await userApi.projectDetail(pid);
          const episodes = Array.isArray(row.episodes) ? row.episodes : [];
          const hit = episodes.find(ep => Number(ep && ep.id) === epId);
          if (hit && Array.isArray(hit.shots) && hit.shots.length) {
            shots = hit.shots.map((s, idx) => ({
              ...s,
              videoUrl: String((s && (s.videoUrl || s.video_url)) || "").trim(),
              shotNo: Number((s && (s.no || s.shotNo)) || idx + 1),
              title: s.title || ""
            }));
          }
        } catch (_) {
          /* 离线或未登录时沿用本地 timelineItems */
        }
      }
      return shots;
    },
    async handleExportEpisodeVideos() {
      if (this.exportingVideos) return;
      if (!this.step4TimelineHydrated) {
        window.alert("分镜数据加载中，请稍候再导出");
        return;
      }
      this.exportingVideos = true;
      try {
        const shots = await this.fetchTimelineShotsForExport();
        const entries = this.collectEpisodeVideoExports(shots);
        if (!entries.length) {
          window.alert("当前分集暂无已生成的视频，无法导出。");
          return;
        }
        for (let i = 0; i < entries.length; i += 1) {
          this.openVideoUrlInNewTab(entries[i].rawUrl);
          if (i < entries.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        }
        if (entries.length > 1) {
          window.alert(`已依次打开 ${entries.length} 个视频链接，请在浏览器下载栏查看。`);
        }
      } finally {
        this.exportingVideos = false;
      }
    },
    async handleBatchGenerateAllVideos() {
      if (this.batchGenerating || this.hasAnyVideoGenActive) return;
      if (!this.step4TimelineHydrated) {
        window.alert("分镜数据加载中，请稍候再批量生成");
        return;
      }
      this.syncCurrentStoryboardPrompt(true);
      if (this.videoPointsPerSecond <= 0) {
        window.alert("当前模型/清晰度未配置扣费价格，请先在后台配置 fa_ai_points_config（step4_generate_video）。");
        return;
      }
      const list = Array.isArray(this.timelineItems) ? this.timelineItems : [];
      if (!list.length) {
        window.alert("暂无可生成的分镜");
        return;
      }
      const allTasks = list
        .map((item, i) => ({ item, i }))
        .filter(x => {
          if (!x || !x.item) return false;
          if (this.shotIsSkipped(x.item)) return false;
          const ep = this.buildStep4PersistContext(x.item);
          const tid = this.resolveShotId(x.item);
          return (
            tid > 0 ||
            (ep.episodeId > 0 && ep.shotNo > 0) ||
            (ep.episodeNo > 0 && ep.shotNo > 0)
          );
        });
      if (!allTasks.length) {
        window.alert("没有可关联后端的分镜（缺少分镜 id 或分集/镜号），请刷新项目后再试。");
        return;
      }
      const withoutVideo = allTasks.filter(x => !String((x.item && x.item.videoUrl) || "").trim());
      const n = withoutVideo.length;
      let tasks = allTasks;
      if (n > 0) {
        const ok = window.confirm(
          `是否对剩余未生成视频的 ${n} 个分镜批量生成？\n（已有视频的分镜将跳过；时间轴上共 ${allTasks.length} 个可分镜。）`
        );
        if (!ok) return;
        tasks = withoutVideo;
      } else {
        const ok = window.confirm(
          `当前时间轴上 ${allTasks.length} 个分镜均已有关联视频。\n是否仍要对全部分镜重新批量生成？`
        );
        if (!ok) return;
      }
      if (!tasks.length) {
        window.alert("没有需要生成的分镜。");
        return;
      }
      this.batchGenerating = true;
      // 受控并发：避免一次性创建过多任务压爆 Worker（后端 maxRunning 通常为 1~2）
      const CONCURRENCY = 2;

      let okCount = 0;
      let failCount = 0;
      const configSavedTasks = [];
      const savedTasks = [];

      // 第0阶段：先批量落库每条分镜的视频参数（duration_seconds / resolution）
      for (let i = 0; i < tasks.length; i++) {
        const row = tasks[i];
        const item = row.item;
        const targetShotId = this.resolveShotId(item);
        const epShotCtx = this.buildStep4PersistContext(item);
        if (
          !(
            targetShotId > 0 ||
            (epShotCtx.episodeId > 0 && epShotCtx.shotNo > 0) ||
            (epShotCtx.episodeNo > 0 && epShotCtx.shotNo > 0)
          )
        ) {
          failCount++;
          continue;
        }
        const safeSeconds = this.clampDurationSeconds(
          this.parseDurationSeconds((item && item.durationText) || this.selectedDuration)
        );
        try {
          const cfg = await userApi.saveStoryboardVideoConfig({
            projectId: Number(this.projectId || 0),
            episodeId: epShotCtx.episodeId,
            episodeNo: epShotCtx.episodeNo,
            shotNo: epShotCtx.shotNo,
            storyboardId: Number(targetShotId || 0),
            shotId: Number(targetShotId || 0),
            duration: `${safeSeconds}s`,
            durationSeconds: Number(safeSeconds || 0),
            resolution: String(this.selectedResolution || "")
          });
          const savedSec = Number((cfg && cfg.durationSeconds) || safeSeconds || 0);
          const savedRes = String((cfg && cfg.resolution) || this.selectedResolution || "");
          const resolvedPk = Number((cfg && cfg.shotId) || 0);
          const idx = (this.timelineItems || []).findIndex(it => {
            const pk = Number(it && it.id);
            const no = Number(it && it.shotNo);
            return (
              (targetShotId > 0 && pk === Number(targetShotId)) ||
              (epShotCtx.shotNo > 0 && no === epShotCtx.shotNo) ||
              (resolvedPk > 0 && pk === resolvedPk)
            );
          });
          if (idx !== -1) {
            this.$set(this.timelineItems, idx, {
              ...this.timelineItems[idx],
              ...(resolvedPk > 0 ? { id: resolvedPk } : {}),
              durationSeconds: savedSec > 0 ? savedSec : this.timelineItems[idx].durationSeconds,
              durationText: savedSec > 0 ? `${savedSec}s` : this.timelineItems[idx].durationText,
              resolution: savedRes || this.timelineItems[idx].resolution
            });
          }
          configSavedTasks.push({
            item: {
              ...item,
              ...(resolvedPk > 0 ? { id: resolvedPk } : {}),
              durationSeconds: savedSec > 0 ? savedSec : safeSeconds,
              durationText: savedSec > 0 ? `${savedSec}s` : `${safeSeconds}s`,
              resolution: savedRes || this.selectedResolution
            },
            i: row.i
          });
        } catch (e) {
          failCount++;
        }
      }

      // 第1阶段：批量保存每条分镜最新 prompt
      const phase1Tasks = configSavedTasks.length ? configSavedTasks : tasks;
      for (let i = 0; i < phase1Tasks.length; i++) {
        const row = phase1Tasks[i];
        const item = row.item;
        const targetShotId = this.resolveShotId(item);
        const epShotCtx = this.buildStep4PersistContext(item);
        if (
          !(
            targetShotId > 0 ||
            (epShotCtx.episodeId > 0 && epShotCtx.shotNo > 0) ||
            (epShotCtx.episodeNo > 0 && epShotCtx.shotNo > 0)
          )
        ) {
          failCount++;
          continue;
        }
        const promptText = this.normalizePromptForBackend(
          this.expandAtImagePlaceholdersInPrompt(String(item && item.prompt ? item.prompt : ""))
        );
        if (!promptText) {
          failCount++;
          continue;
        }
        try {
          const saveRes = await userApi.saveStoryboardPrompt({
            projectId: Number(this.projectId || 0),
            episodeId: epShotCtx.episodeId,
            episodeNo: epShotCtx.episodeNo,
            shotNo: epShotCtx.shotNo,
            storyboardId: Number(targetShotId || 0),
            shotId: Number(targetShotId || 0),
            prompt: promptText
          });
          const savedPrompt = this.normalizePromptForBackend(
            this.expandAtImagePlaceholdersInPrompt(String((saveRes && saveRes.prompt) || promptText))
          );
          const resolvedPk = Number((saveRes && saveRes.shotId) || 0);
          const idx = (this.timelineItems || []).findIndex(it => {
            const pk = Number(it && it.id);
            const no = Number(it && it.shotNo);
            return (
              (targetShotId > 0 && pk === Number(targetShotId)) ||
              (epShotCtx.shotNo > 0 && no === epShotCtx.shotNo) ||
              (resolvedPk > 0 && pk === resolvedPk)
            );
          });
          if (idx !== -1) {
            this.$set(this.timelineItems, idx, {
              ...this.timelineItems[idx],
              ...(resolvedPk > 0 ? { id: resolvedPk } : {}),
              prompt: savedPrompt
            });
          }
          this.updateShotPromptInSessionCache(Number(resolvedPk || item.id || targetShotId), savedPrompt);
          const promptReferences = this.buildReferencesFromPromptText(savedPrompt);
          savedTasks.push({
            item: {
              ...item,
              ...(resolvedPk > 0 ? { id: resolvedPk } : {}),
              prompt: savedPrompt,
              references: promptReferences
            },
            i: row.i
          });
        } catch (e) {
          failCount++;
        }
      }

      const runOne = async ({ item, i }) => {
        const targetShotId = this.resolveShotId(item);
        const epShotCtx = this.buildStep4PersistContext(item);
        if (
          !(
            targetShotId > 0 ||
            (epShotCtx.episodeId > 0 && epShotCtx.shotNo > 0) ||
            (epShotCtx.episodeNo > 0 && epShotCtx.shotNo > 0)
          )
        ) {
          failCount++;
          return;
        }
        const promptText = this.normalizePromptForBackend(
          this.expandAtImagePlaceholdersInPrompt(String(item && item.prompt ? item.prompt : ""))
        );
        if (!promptText) {
          failCount++;
          return;
        }
        const safeSeconds = this.clampDurationSeconds(
          this.parseDurationSeconds((item && item.durationText) || this.selectedDuration)
        );
        const refsForTask = Array.isArray(item && item.references)
          ? item.references
          : this.buildReferencesFromPromptText(promptText);
        const backendVideoPrompt = this.normalizePromptForBackend(
          this.promptDisplayToBackendAtImage(promptText, refsForTask)
        );
        const createRes = await userApi.createGenerateStoryboardVideoTask({
          projectId: Number(this.projectId || 0),
          episodeId: epShotCtx.episodeId,
          episodeNo: epShotCtx.episodeNo,
          shotNo: epShotCtx.shotNo,
          storyboardId: Number(targetShotId || 0),
          shotId: Number(targetShotId || 0),
          modelCode: String(this.selectedModel || ""),
          resolution: String(this.selectedResolution || ""),
          durationSeconds: Number(safeSeconds || 0),
          prompt: backendVideoPrompt,
          references: JSON.stringify(refsForTask)
        });
        const taskId = Number(createRes.taskId || 0);
        if (!taskId) {
          throw new Error("任务创建失败");
        }
        const batchShotId = Number(targetShotId || item.id);
        this.registerVideoGenTask(batchShotId, { taskId, status: "pending" });
        const result = await this.runVideoGenPoll(
          batchShotId,
          taskId,
          Number(createRes.pollIntervalMs || 10000)
        );
        this.applyVideoGenSuccess(batchShotId, result, {
          savedPrompt: promptText,
          backendPrompt: backendVideoPrompt,
          durationSeconds: safeSeconds,
          cover: item.cover,
          model: this.selectedModel,
          resolution: this.selectedResolution
        });
        okCount++;
      };

      const runPool = async (items, concurrency) => {
        const queue = items.slice();
        const workers = new Array(Math.max(1, concurrency)).fill(0).map(async () => {
          while (queue.length) {
            const it = queue.shift();
            if (!it) continue;
            try {
              await runOne(it);
            } catch (e) {
              const failShotId = Number((it && it.item && it.item.id) || 0);
              if (failShotId > 0) this.markVideoGenFailed(failShotId, e);
              failCount++;
            }
          }
        });
        await Promise.all(workers);
      };

      try {
        await runPool(savedTasks, CONCURRENCY);
      } finally {
        this.batchGenerating = false;
      }
      if (okCount > 0) this.notifyPointsChanged();
      window.alert(`批量生成完成：成功 ${okCount}，失败 ${failCount}`);
    },
    openRecordModal(item) {
      this.currentRecordItemId = item.id;
      this.recordModalVisible = true;
    },
    closeRecordModal() {
      this.recordModalVisible = false;
      this.currentRecordItemId = null;
    },
    deleteStoryboardItem(item) {
      const confirmText = `确认删除分镜 #${item.id} 及其全部历史视频记录吗？`;
      if (!window.confirm(confirmText)) return;

      this.timelineItems = this.timelineItems.filter(row => row.id !== item.id);
      delete this.historyRecordsByStoryboardId[item.id];

      if (this.storyboard.id === item.id) {
        this.storyboard = this.timelineItems[0] || {
          id: 0,
          shotId: 0,
          title: "暂无分镜",
          cover: "https://dummyimage.com/108x146/2b2b2b/fff.png&text=0",
          durationText: "0s"
        };
        this.description = this.normalizePromptForDisplay(String(this.storyboard.prompt || ""));
        if (this.$refs.promptEditor) {
          this.$refs.promptEditor.innerText = this.description || "";
        }
      }
      this.recalcTotalDurationAndBind();
    }
  }
};
</script>

<style scoped>
.modal {
  position: relative;
}

.modal-close {
    position: absolute;
    right: 14px;
    top: 10px;
    width: 36px;
    height: 36px;
    border: none;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.06);
    color: #fff;
    font-size: 22px;
    line-height: 1;
    cursor: pointer;
}

.editor-page {
  color: #fff;
  height: calc(100vh - 30px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.editor-topbar {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 12px;
  margin-bottom: 30px;
}

.editor-top-left,
.editor-top-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.editor-top-right {
  justify-content: flex-end;
}

.editor-fixed-actions {
  position: absolute;
  top: 20px;
  right: 14px;
  z-index: 5;
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: nowrap;
}

.back-btn {
  border: 0;
  border-radius: 8px;
  background: #333333;
  color: #fff;
  padding: 8px 14px;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
}

.episode-title {
  max-width: 320px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.editor-layout {
  display: grid;
  grid-template-columns: 270px 1fr 230px;
  gap: 10px;
  flex: 1;
  min-height: 0;
  align-items: stretch;
}

.editor-left,
.editor-center,
.editor-right,
.script-assets,
.script-main {
  background: #333333;
  border-radius: 5px;
  padding: 10px;
}

.editor-left,
.editor-center,
.editor-right {
  min-height: 0;
}

.editor-left {
  overflow-y: auto;
}

.editor-center {
  display: flex;
  flex-direction: column;
}

.left-tab {
  border: 0;
  border-radius: 10px;
  height: 40px;
  color: #fff;
  background: #1f1f1f;
  cursor: pointer;
}

.left-tab.active {
  background: #165a3d;
}

.left-section + .left-section {
  margin-top: 16px;
}

.left-section h3,
.editor-right h3,
.script-assets h3,
.script-main h3 {
  margin-top: 0;
}

.field-label {
  color: #cbcbcb;
  margin-bottom: 8px;
}

.select-mock {
  height: 38px;
  background: #1f1f1f;
  border: 1px solid #3c3c3c;
  border-radius: 8px;
  display: flex;
  align-items: center;
  padding: 0 12px;
}

.select-input {
  width: 100%;
  height: 38px;
  background: #1f1f1f;
  border: 1px solid #3c3c3c;
  border-radius: 8px;
  color: #fff;
  padding: 0 10px;
}

.inline-selects {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-top: 10px;
}

.asset-actions {
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
}

.asset-btn {
  border: 1px solid #3f3f3f;
  border-radius: 8px;
  background: #1f1f1f;
  color: #d9d9d9;
  padding: 8px;
  cursor: pointer;
}

.asset-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-right: 56px;
  margin-top: 2px;
  margin-bottom: 8px;
}

.asset-modal-header h3 {
  margin: 0;
}

.tabs-with-gap {
  margin-top: 4px;
}

.asset-strip-hint {
  margin: 6px 0 0;
  font-size: 14px;
  line-height: 1.45;
  color: #9a9a9a;
}

.asset-strip-context-hint {
  margin: 4px 0 0;
  font-size: 14px;
  line-height: 1.45;
  color: #c9a227;
}

.asset-strip-group {
  margin-top: 10px;
}

.asset-strip-caption {
  font-size: 14px;
  color: #b0b0b0;
  margin-bottom: 6px;
}

.asset-strip-empty {
  font-size: 14px;
  color: #666;
  line-height: 54px;
}

.asset-modal-sub {
  margin: 0 0 10px;
  font-size: 14px;
  color: #9a9a9a;
  line-height: 1.45;
}

.asset-strip-bound-rows {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 4px;
}

.asset-strip-subrow {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.asset-strip-type-label {
  flex: 0 0 auto;
  font-size: 14px;
  color: #8a8a8a;
  letter-spacing: 0.02em;
}

.asset-strip-row-empty {
  font-size: 14px;
  color: #5c5c5c;
  line-height: 54px;
  padding-left: 4px;
}

.asset-strip--sub {
  margin-top: 0;
  min-height: 48px;
}

.asset-strip {
  display: flex;
  gap: 8px;
  margin-top: 4px;
  flex-wrap: wrap;
  align-items: center;
  min-height: 54px;
}

.asset-thumb {
  width: 54px;
  height: 54px;
  border-radius: 8px;
  background: #5d5d5d;
  background-size: cover;
  background-position: center;
  flex: 0 0 auto;
}
.asset-thumb.is-clickable {
  cursor: pointer;
}

.asset-thumb-wrap {
  position: relative;
  flex: 0 0 auto;
}

.asset-thumb-remove {
  position: absolute;
  top: -6px;
  right: -6px;
  z-index: 2;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.65);
  color: #fff;
  font-size: 14px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.asset-thumb-remove:hover {
  background: rgba(220, 60, 60, 0.95);
}

.desc-box {
  width: 100%;
  background: #1a1a1a;
  border: 1px solid #3a3a3a;
  border-radius: 10px;
  color: #fff;
  padding: 12px;
  resize: none;
  overflow-y: auto;
}

.prompt-editor-wrap {
  position: relative;
  flex: 1;
  min-height: 0;
  max-height: 230px;
  overflow: visible;
}

.prompt-editor {
  height: 100%;
  min-height:220px;
  max-height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  outline: none;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.prompt-editor:empty::before {
  content: attr(data-placeholder);
  color: #8e8e8e;
}

.mention-panel {
  position: absolute;
  left: 0;
  bottom: calc(100% + 8px);
  width: 100%;
  max-height: 180px;
  overflow-y: auto;
  background: #242424;
  border: 1px solid #3a3a3a;
  border-radius: 10px;
  padding: 6px;
  z-index: 3;
}

.mention-item {
  width: 100%;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #fff;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 10px;
  cursor: pointer;
}

.mention-item:hover {
  background: #333333;
}

.mention-item-main {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
}

.mention-thumb {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  object-fit: cover;
}

.mention-thumb--empty {
  display: inline-block;
  background: #3a3a3a;
}

.mention-name {
  color: #e9e9e9;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

.mention-type {
  color: #9e9e9e;
  font-size: 14px;
}

.mention-empty {
  padding: 10px 12px;
  color: #9e9e9e;
  font-size: 14px;
}

:deep(.mention-tag) {
  display: inline-block;
  margin: 0 2px;
  padding: 1px 6px;
  border-radius: 999px;
  background: #19b06b;
  color: #fff;
  font-size: 14px;
  line-height: 16px;
  white-space: nowrap;
  vertical-align: middle;
}

:deep(.mention-tag-img) {
  width: 16px;
  height: 16px;
  border-radius: 999px;
  object-fit: cover;
  margin-right: 4px;
  vertical-align: -2px;
}

:deep(.mention-tag-text) {
  display: inline-block;
}

.asset-library-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-top: 12px;
}

.asset-library-item {
  min-width: 0;
  background: #252525;
  border: 1px solid #3a3a3a;
  border-radius: 10px;
  padding: 8px;
  cursor: pointer;
}

.asset-library-item.active {
  border-color: #19b06b;
  box-shadow: inset 0 0 0 1px #19b06b;
}

.asset-library-thumb {
  width: 100%;
  height: 90px;
  object-fit: cover;
  border-radius: 8px;
  margin-bottom: 6px;
}

.asset-library-item span {
  display: block;
  font-size: 14px;
  color: #eaeaea;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.preview-stage {
  background: #111;
  border-radius: 10px;
  padding: 5px;
  flex: 0 0 470px;
  min-height: 490px;
}

.main-preview {
  /* width: 100%; */
  display: block;
  margin: 0 auto;
  width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 10px;
}

.placeholder-block {
  background: #000 !important;
}

.video-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
}

.video-placeholder-card {
  width: 44px;
  height: 30px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.35);
  background: rgba(20, 20, 20, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.35);
}

.video-placeholder-play {
  display: inline-block;
  width: 0;
  height: 0;
  border-top: 6px solid transparent;
  border-bottom: 6px solid transparent;
  border-left: 10px solid rgba(255, 255, 255, 0.92);
  margin-left: 2px;
}

.center-desc-wrap {
  background: #242424;
  border-radius: 10px;
  padding: 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex-shrink: 0;
}

.desc-footer-actions {
  margin-top: 10px;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
}

.desc-points-text {
  font-size: 14px;
}

.desc-short-warning {
  font-size: 14px;
  color: #ff9c66;
  background: rgba(255, 156, 102, 0.12);
  border: 1px solid rgba(255, 156, 102, 0.35);
  border-radius: 999px;
  padding: 2px 10px;
  white-space: nowrap;
}

.desc-generate-btn {
  min-width: auto;
  padding: 0 16px;
  white-space: nowrap;
  display: block;
  margin: 0px auto 10px auto;
}

.timeline-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.right-timeline-toolbar {
}

.timeline-time{
  font-size:12px;
}

.small-btn {
  min-width: auto;
  padding: 0 14px;
}

.timeline-strip {
  display: flex;
  gap: 10px;
  overflow-x: auto;
}

.timeline-item {
  min-width: 110px;
  padding: 8px;
  border-radius: 10px;
  background: #1f1f1f;
  border: 1px solid #333;
}

.timeline-item.active {
  border-color: #19b06b;
}

.timeline-item img {
  width: 100%;
  height: 78px;
  object-fit: cover;
  border-radius: 8px;
}

.timeline-item span,
.timeline-item small {
  display: block;
  margin-top: 6px;
}

.record-card {
  background: #252525;
  border: 1px solid #3b3b3b;
  border-radius: 10px;
  overflow: hidden;
}

.record-card img {
  width: 100%;
  height: 120px;
  object-fit: cover;
}

.record-body {
  padding: 10px;
}

.record-status {
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
}

.success-tag {
  color: #19b06b;
}

.export-btn {
  max-width: 120px;
}

.editor-right {
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

.storyboard-list-scroll {
  flex: 1;
  min-height: 0;
  max-height: none;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.storyboard-list-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
  border: 1px solid #3b3b3b;
  border-radius: 10px;
  background: #252525;
  padding: 8px;
  cursor: pointer;
}

.storyboard-list-item.active {
  border-color: var(--app-accent);
  box-shadow: inset 0 0 0 1px var(--app-accent);
}

.shot-actions-row {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  padding: 6px;
  background: linear-gradient(180deg, rgba(25,176,107,0.08), rgba(25,176,107,0.02));
  border: 1px dashed rgba(25,176,107,0.4);
  border-radius: 8px;
}

.shot-action-btn {
  flex: 1 1 auto;
  min-width: 0;
  padding: 5px 8px;
  font-size: 14px;
  border-radius: 6px;
  border: 1px solid transparent;
  background: rgba(255,255,255,0.04);
  color: #cfd8dc;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}
.shot-action-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.shot-action-btn.shot-action-regen {
  background: var(--app-bg-elev-3);
  border-color: var(--app-text-soft);
  color: var(--app-text);
}
.shot-action-btn.shot-action-skip {
  background: transparent;
  border-color: var(--app-border);
  color: var(--app-text-soft);
}
.shot-action-btn.shot-action-skip.on {
  background: var(--app-warn-soft);
  border-color: var(--app-warn);
  color: var(--app-warn);
}

.storyboard-skip-badge {
  position: absolute;
  top: 6px;
  left: 6px;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--app-text);
  color: var(--app-bg);
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.5px;
}

.editor-right-footer {
  flex-shrink: 0;
  padding: 10px 4px 4px;
  border-top: 1px solid #3b3b3b;
  margin-top: 4px;
}
.next-episode-btn {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  padding: 8px 10px;
  text-align: left;
}
.next-episode-sub {
  font-style: normal;
  font-size: 14px;
  opacity: 0.7;
}

.storyboard-thumb {
  width: 100%;
  height: 90px;
  object-fit: cover;
  border-radius: 8px;
}

.storyboard-thumb-wrap {
  position: relative;
}

.storyboard-item-actions {
  position: absolute;
  right: 6px;
  top: 6px;
  display: flex;
  gap: 6px;
}

.icon-btn {
  width: 26px;
  height: 26px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.4);
  background: rgba(0, 0, 0, 0.45);
  color: #fff;
  cursor: pointer;
  line-height: 1;
  font-size: 14px;
  padding: 0;
}

.icon-btn.danger {
  border-color: rgba(255, 120, 120, 0.8);
}

.storyboard-item-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}

.storyboard-item-meta small {
  display: inline-block;
  color: #bcbcbc;
}

.storyboard-meta-right {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.storyboard-gen-tag {
  font-size: 14px;
  line-height: 1.2;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--app-bg-elev-3);
  color: var(--app-text-soft);
  white-space: nowrap;
}

.storyboard-item-meta.is-generating strong {
  color: var(--app-warn);
}
.storyboard-item-meta.is-failed strong {
  color: var(--app-danger);
}
.storyboard-item-meta.is-done strong {
  color: var(--app-primary);
}

.storyboard-gen-tag.is-failed {
  background: var(--app-danger-soft);
  color: var(--app-danger);
}
.storyboard-gen-tag.is-generating {
  background: var(--app-warn-soft);
  color: var(--app-warn);
}
.storyboard-gen-tag.is-idle {
  background: var(--app-bg-elev-3);
  color: var(--app-text-soft);
}
.storyboard-gen-tag.is-done {
  background: var(--app-primary-soft);
  color: var(--app-primary);
}

.storyboard-item-meta.is-idle strong {
  color: #b0b0b0;
}

.storyboard-item-meta.is-done strong {
  color: #8fd4a8;
}

.record-modal-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height:420px;
}

.record-modal-item {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 10px;
  border: 1px solid #3b3b3b;
  border-radius: 10px;
  background: #252525;
  padding: 8px;
}

.record-download-btn {
  flex-shrink: 0;
  align-self: center;
  margin: 0 2px 0 0;
}

.record-modal-body {
  flex: 1;
  min-width: 0;
}

.record-modal-thumb {
  width: 120px;
  height: 72px;
  object-fit: cover;
  border-radius: 8px;
}

.record-modal-item small {
  display: block;
  margin: 6px 0;
  color: #bcbcbc;
}

.empty-record {
  min-height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #b5b5b5;
}

/* 资产弹窗底部：与全局 .main-btn { width:100% } 冲突时避免两按钮断行，并保持同宽 */
.modal-actions-equal {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
}

.modal-actions-equal .modal-footer-btn {
  flex: 1 1 0;
  min-width: 0;
  width: auto;
  max-width: none;
}

.prompt-template-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin: 8px 0 4px;
  padding: 8px 10px;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid #3a3a3a;
}

.prompt-template-label {
  font-size: 14px;
  color: #9a9a9a;
  margin-right: 4px;
}

.prompt-tpl-btn {
  font-size: 14px;
  padding: 4px 10px;
  min-height: 28px;
}

/* 手机 / 窄屏：三栏改单列纵向排布，避免 270+230 固定列挤压 */
@media (max-width: 768px) {
  .editor-page {
    height: auto;
    min-height: calc(100dvh - 56px);
    max-height: none;
    overflow-x: hidden;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    padding-bottom: max(16px, env(safe-area-inset-bottom, 0px));
    box-sizing: border-box;
  }

  .editor-topbar {
    margin-bottom: 10px;
    padding: 0 2px;
  }

  .episode-title {
    max-width: 100%;
    white-space: normal;
    font-size: 15px;
    line-height: 1.4;
  }

  .editor-fixed-actions {
    position: static;
    z-index: auto;
    width: 100%;
    flex-wrap: wrap;
    justify-content: stretch;
    gap: 8px;
    padding: 0 2px 12px;
    box-sizing: border-box;
  }

  .editor-fixed-actions .back-btn,
  .editor-fixed-actions .main-btn {
    flex: 1 1 100%;
    width: 100%;
    min-width: 0;
    max-width: none;
    font-size: 13px;
    padding: 10px 12px;
    white-space: normal;
    line-height: 1.35;
    text-align: center;
  }

  .editor-layout {
    display: flex;
    flex-direction: column;
    flex: none;
    gap: 12px;
    min-height: 0;
    width: 100%;
  }

  .editor-left,
  .editor-center,
  .editor-right {
    width: 100%;
    min-width: 0;
    max-width: 100%;
    overflow: visible;
    box-sizing: border-box;
  }

  .editor-center {
    order: 1;
  }

  .editor-right {
    order: 2;
    max-height: none;
    overflow: visible;
  }

  .editor-left {
    order: 3;
    max-height: none;
    overflow: visible;
  }

.preview-stage {
  flex: none;
  width: 100%;
  min-height: 0;
  height: auto;
  aspect-ratio: 16 / 9;
  max-height: min(52vw, 300px);
}

.main-preview {
  display: block;
  margin: 0 auto;
  width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 10px;
}

  .center-desc-wrap {
    flex: none;
    width: 100%;
  }

  .prompt-editor-wrap {
    max-height: none;
    min-height: 120px;
  }

  .prompt-editor {
    min-height: 120px;
    max-height: none;
  }

  .desc-footer-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .desc-generate-btn {
    width: 100%;
    max-width: none;
    white-space: normal;
    line-height: 1.4;
    padding: 12px 14px;
    text-align: center;
  }

  .timeline-toolbar {
    flex-wrap: wrap;
    align-items: flex-start;
    gap: 6px;
  }

  .timeline-toolbar h3 {
    font-size: 15px;
    margin: 0;
  }

  .prompt-template-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .prompt-tpl-btn {
    width: 100%;
    text-align: left;
  }

  .editor-right h3 {
    font-size: 15px;
    margin-bottom: 8px;
  }

  .storyboard-list-scroll {
    flex-direction: row;
    flex-wrap: nowrap;
    overflow-x: auto;
    overflow-y: hidden;
    gap: 10px;
    padding-bottom: 8px;
    -webkit-overflow-scrolling: touch;
    scroll-snap-type: x proximity;
  }

  .storyboard-list-item {
    flex: 0 0 min(44vw, 168px);
    min-width: 0;
    scroll-snap-align: start;
  }

  .storyboard-thumb {
    height: auto;
    aspect-ratio: 16 / 10;
  }

  .storyboard-meta-right {
    flex-wrap: wrap;
    justify-content: flex-end;
  }

  .storyboard-gen-tag {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .inline-selects {
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }

  .asset-strip-bound-rows {
    gap: 8px;
  }

  .mention-panel {
    max-height: min(40vh, 200px);
  }
}

@media (max-width: 400px) {
  .inline-selects {
    grid-template-columns: 1fr;
  }
}
</style>
